/* eslint-disable dot-notation */
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import { CapsuleGeometry } from '@/geometry/CapsuleGeometry';
import type {
  MainModule,
  MjModel,
  MjData,
  MjvOption,
  MjvPerturb,
  MjvCamera,
  MjvScene,
  MjvGeom,
} from './mujocoTypes';
import type { CameraMode, TrajectoryData } from '@/types/scene.types';
import { CAMERA_CONFIG } from './cameraConfig';

/**
 * Main application class that manages the MuJoCo physics simulation
 * and Three.js rendering with advanced texture support.
 */

export class MujocoApp {
  mjModel: MjModel | null = null;
  mjData: MjData | null = null;
  mjvOption: MjvOption;
  mjvPerturb: MjvPerturb;
  mjvCamera: MjvCamera;
  mjvScene: MjvScene | null = null;

  paused = false;
  frameId: number | null = null;
  maxGeoms: number = 2 ** 15;

  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  meshes: THREE.Mesh[] = [];
  bufferGeometryCache = new Map<string, THREE.BufferGeometry>();

  // Texture management
  textureCache = new Map<number, THREE.Texture>();
  textureCacheByFilename = new Map<string, THREE.Texture>();
  materialToTextureMap = new Map<number, string>();
  materialNameToTextureMap = new Map<string, string>();
  discoveredTextures: string[] = [];
  materialIdToTexturePath = new Map<number, string>();
  materialIdToMeshes = new Map<number, THREE.Mesh[]>();

  private cameraMode: CameraMode = 'top-down';
  private mujoco: MainModule;
  private frameCount = 0;
  private lastFpsLog = performance.now();
  private lights: THREE.Light[] = [];
  private sceneCentered = false;
  private raycaster = new THREE.Raycaster();

  // Robot trajectory data
  private trajectoryData: TrajectoryData | null = null;
  private currentTrajectoryFrame = 0;
  private trajectoryPlaying = false;
  private trajectoryAnimationId: number | null = null;
  private lastTrajectoryUpdateTime = 0;

  // Robot repeating action (from app_clean.ts)
  private repeatActionPlaying = false;
  private repeatActionCallback: (() => void) | null = null;

  // Action JSON data for current scene action
  private actionJson: any = null;
  private allActionsJson: any = null;
  private robotInitParameters: any = null;

  // Action playback state
  private actionPlaybackIndex: number = 0;
  private secondsSinceLastAction: number = 0.0;
  private playing: boolean = false;

  // @ts-expect-error
  private activeActionIndex: number = 0;
  // @ts-expect-error
  private objectOverlayPositions: any = null;

  constructor(
    mujoco: MainModule,
    containerElementId: string = 'mujoco-sim',
    cameraMode: CameraMode = 'top-down'
  ) {
    this.mujoco = mujoco;

    this.mjvPerturb = new mujoco.MjvPerturb();
    this.mjvOption = new mujoco.MjvOption();
    this.mjvCamera = new mujoco.MjvCamera();

    this.scene = new THREE.Scene();

    this.renderer = new THREE.WebGLRenderer();
    const element = document.getElementById(containerElementId);
    if (!element) {
      throw new Error(`No element '${containerElementId}' where the renderer would be placed.`);
    }

    const rect = element.getBoundingClientRect();
    const width = rect.width || element.clientWidth || 800;
    const height = rect.height || element.clientHeight || 600;
    this.renderer.setSize(width, height);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.display = 'block';

    element.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      45,
      width / height, // Use container aspect ratio, not window
      0.1,
      1000
    );
    this.camera.up.set(0, 0, 1);
    this.camera.position.set(-2, 0, 2);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);

    this.setCameraMode(cameraMode);
    this.setupClickHandler();
    this.setupCameraLogging();
    this.setupResizeHandler(containerElementId);
  }

  /**
   * Sets up resize handler to update renderer and camera when container size changes
   */
  private setupResizeHandler(containerElementId: string): void {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;

        // Update renderer size
        this.renderer.setSize(width, height);

        // Update camera aspect ratio
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        console.log(`Resized to ${width}x${height}, aspect: ${(width / height).toFixed(3)}`);
      }
    });

    const element = document.getElementById(containerElementId);
    if (element) {
      resizeObserver.observe(element);
    }

    // Store observer to dispose later
    (this as any)._resizeObserver = resizeObserver;
  }

  /**
   * Sets up keyboard handler to log camera position when user presses 'L'
   */
  private setupCameraLogging(): void {
    window.addEventListener('keydown', (event) => {
      // Press 'L' to log camera position
      if (event.key === 'l' || event.key === 'L') {
        this.logCameraPosition();
      }
    });
  }

  /**
   * Logs the current camera position and target to console in a copy-paste friendly format
   */
  logCameraPosition(): void {
    const pos = this.camera.position;
    const target = this.controls.target;

    console.log('\n=== 📸 CAMERA POSITION ===');
    console.log(`Mode: ${this.cameraMode}`);
    console.log(`Position: (${pos.x.toFixed(3)}, ${pos.y.toFixed(3)}, ${pos.z.toFixed(3)})`);
    console.log(`Target: (${target.x.toFixed(3)}, ${target.y.toFixed(3)}, ${target.z.toFixed(3)})`);
    console.log('\n// Copy-paste friendly:');
    console.log(
      `camera.position.set(${pos.x.toFixed(3)}, ${pos.y.toFixed(3)}, ${pos.z.toFixed(3)});`
    );
    console.log(
      `controls.target.set(${target.x.toFixed(3)}, ${target.y.toFixed(3)}, ${target.z.toFixed(3)});`
    );
    console.log('========================\n');
  }

  /**
   * Sets up click handler for top-down view position detection
   */
  private setupClickHandler(): void {
    this.renderer.domElement.addEventListener('click', (event) => {
      // Only handle clicks in top-down mode
      if (this.cameraMode !== 'top-down') {
        return;
      }

      // Get the bounding rectangle of the canvas
      const rect = this.renderer.domElement.getBoundingClientRect();

      // Convert mouse coordinates to normalized device coordinates (-1 to +1)
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Update the raycaster with camera and mouse position
      this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);

      // Find intersections with scene meshes
      const intersects = this.raycaster.intersectObjects(this.meshes, false);

      if (intersects.length > 0 && intersects[0]) {
        // Get the first intersection point
        const point = intersects[0].point;

        // Convert 3D world position back to screen percentage
        const screenPercent = this.worldToScreenPercentage(point);

        console.log(
          `Clicked 3D position: x=${point.x.toFixed(3)}, y=${point.y.toFixed(3)}, z=${point.z.toFixed(3)}`
        );
        if (screenPercent) {
          console.log(
            `Screen percentage: x=${screenPercent.x.toFixed(1)}%, y=${screenPercent.y.toFixed(1)}%`
          );
          console.log(
            `For scenes.json: "position": {"x": ${screenPercent.x.toFixed(3)}, "y": ${screenPercent.y.toFixed(3)}}`
          );
        }
      }
    });
  }

  /**
   * Raycasts from a percentage position (0-100) on the canvas to find 3D world coordinates.
   * Used for location overlays.
   * @param percentX - X position as percentage (0-100)
   * @param percentY - Y position as percentage (0-100)
   * @returns The 3D world coordinates if intersection found, null otherwise
   */
  raycastFromPercentage(percentX: number, percentY: number): THREE.Vector3 | null {
    // Convert percentage to normalized device coordinates (-1 to +1)
    let x = (percentX / 100) * 2 - 1;
    let y = -(percentY / 100) * 2 + 1; // Flip Y because canvas Y is inverted

    // Compensate for aspect ratio differences in top-down mode
    if (this.cameraMode === 'top-down') {
      const aspect = this.camera.aspect;
      if (aspect > 1) {
        // Wide screen: compress X coordinates to match original proportions
        x /= aspect;
      } else if (aspect < 1) {
        // Tall screen: compress Y coordinates to match original proportions
        y *= aspect;
      }
    }

    console.log(
      `🎯 Raycasting from (${percentX.toFixed(1)}%, ${percentY.toFixed(1)}%) -> NDC(${x.toFixed(3)}, ${y.toFixed(3)}) in ${this.cameraMode} mode`
    );
    console.log(
      `   Canvas size: ${this.renderer.domElement.width}x${this.renderer.domElement.height}`
    );
    console.log(`   Camera aspect: ${this.camera.aspect.toFixed(3)}, FOV: ${this.camera.fov}°`);
    console.log(
      `   Camera position: (${this.camera.position.x.toFixed(2)}, ${this.camera.position.y.toFixed(2)}, ${this.camera.position.z.toFixed(2)})`
    );

    // Update the raycaster
    this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);

    // Find intersections
    const intersects = this.raycaster.intersectObjects(this.meshes, false);

    if (intersects.length > 0 && intersects[0]) {
      const point = intersects[0].point;
      console.log(
        `   ✅ Hit at 3D position: (${point.x.toFixed(3)}, ${point.y.toFixed(3)}, ${point.z.toFixed(3)})`
      );
      return point.clone();
    }

    console.log(`   ❌ No intersection found`);
    return null;
  }

  /**
   * Gets the bounding box of the entire scene
   */
  private getSceneBounds(): THREE.Box3 {
    const bbox = new THREE.Box3();
    this.meshes.forEach((mesh) => bbox.expandByObject(mesh));
    return bbox;
  }

  /**
   * Moves camera to a specific 3D position and switches to 1st-person mode.
   * If trajectory data exists, respects the current camera mode.
   * @param position - The target 3D position (from raycast hit)
   */
  moveTo3DPosition(position: THREE.Vector3): void {
    console.log(
      `🎥 Moving camera to raycast hit: (${position.x.toFixed(3)}, ${position.y.toFixed(3)}, ${position.z.toFixed(3)})`
    );

    // Always switch to 3rd-person for robot scene visualization
    // Skip centering because we'll position the camera manually
    this.setCameraMode('3rd-person', true);

    // Get scene bounds for clamping
    const sceneBounds = this.getSceneBounds();

    // Find the floor level by raycasting downward from the hit point
    const downRaycaster = new THREE.Raycaster();

    // Raycast from above the hit point straight down to find the actual floor
    const rayStart = new THREE.Vector3(position.x, position.y, sceneBounds.max.z + 10);
    const rayDirection = new THREE.Vector3(0, 0, -1); // Point straight down
    downRaycaster.set(rayStart, rayDirection);

    const downIntersects = downRaycaster.intersectObjects(this.meshes, false);

    let floorZ = position.z; // Default to hit point Z

    if (downIntersects.length > 0) {
      // Find the LOWEST intersection (actual floor)
      // Raycasting from above, intersections are sorted by distance from ray origin
      // The FIRST intersection is the TOP (ceiling), LAST is the BOTTOM (floor)
      console.log(`   Found ${downIntersects.length} intersections:`);
      downIntersects.forEach((hit, i) => {
        console.log(`     [${i}] z=${hit.point.z.toFixed(3)}`);
      });

      // Use the LAST (lowest) intersection as the actual floor
      const floorIntersect = downIntersects[downIntersects.length - 1];
      if (floorIntersect) {
        floorZ = floorIntersect.point.z;
        console.log(`   Using LAST intersection as floor: z=${floorZ.toFixed(3)}`);
      }
    } else {
      console.log(`   No floor found below, using hit point z=${floorZ.toFixed(3)}`);
    }

    // Handle camera positioning based on mode
    if (this.cameraMode === '3rd-person') {
      // 3rd-person: Position camera at a good viewing angle
      // Camera is positioned back and to the side from the clicked location
      const cameraHeight = 1.3; // Camera Z position + 1 foot (0.3048 meters)
      const cameraOffsetX = 1.278; // X offset from target (1.699 - 0.421)
      const cameraOffsetY = 0.871; // Y offset from target (1.412 - 0.641)

      console.log(`   🎯 Using offsets: X=${cameraOffsetX}, Y=${cameraOffsetY}, Z=${cameraHeight}`);
      console.log(
        `   🎯 Clicked position: (${position.x.toFixed(3)}, ${position.y.toFixed(3)}, ${position.z.toFixed(3)})`
      );

      // Position camera with offsets from the clicked location
      this.camera.position.set(
        position.x + cameraOffsetX,
        position.y + cameraOffsetY,
        cameraHeight
      );

      // Look at the clicked location at 1 meter height
      this.controls.target.set(position.x, position.y, 1.0);

      console.log(
        `   3rd-person: Camera at (${this.camera.position.x.toFixed(3)}, ${this.camera.position.y.toFixed(3)}, ${this.camera.position.z.toFixed(3)})`
      );
      console.log(
        `   Looking at: (${this.controls.target.x.toFixed(3)}, ${this.controls.target.y.toFixed(3)}, ${this.controls.target.z.toFixed(3)})`
      );
    } else {
      // 1st-person: Position at eye height
      const eyeHeight = CAMERA_CONFIG.firstPerson.eyeHeight;
      const ceilingClearance = CAMERA_CONFIG.firstPerson.ceilingClearance;

      // Calculate desired camera Z position
      let cameraZ = floorZ + eyeHeight;

      // Clamp camera position to stay within scene bounds (don't go through ceiling or floor)
      const maxZ = sceneBounds.max.z - ceilingClearance;
      const minZ = sceneBounds.min.z + eyeHeight;
      cameraZ = Math.max(minZ, Math.min(maxZ, cameraZ));

      if (cameraZ !== floorZ + eyeHeight) {
        console.log(
          `   ⚠️  Camera clamped from z=${(floorZ + eyeHeight).toFixed(3)} to z=${cameraZ.toFixed(3)} (bounds: ${minZ.toFixed(3)} - ${maxZ.toFixed(3)})`
        );
      }

      // Position camera at clamped height
      this.camera.position.set(position.x, position.y, cameraZ);

      // Look backward from this position (in -Y direction)
      this.controls.target.set(position.x, position.y - 1, cameraZ);

      console.log(
        `   1st-person: Camera at (${this.camera.position.x.toFixed(3)}, ${this.camera.position.y.toFixed(3)}, ${this.camera.position.z.toFixed(3)})`
      );
      console.log(
        `   Looking at: (${this.controls.target.x.toFixed(3)}, ${this.controls.target.y.toFixed(3)}, ${this.controls.target.z.toFixed(3)})`
      );
    }

    this.controls.update();
  }

  /**
   * Converts a 3D world position to screen percentage coordinates (0-100).
   * This is the inverse of raycastFromPercentage - useful for placing overlay markers.
   * @param worldPosition - The 3D world coordinates
   * @returns Object with x and y as percentages (0-100), or null if position is behind camera
   */
  worldToScreenPercentage(worldPosition: THREE.Vector3): { x: number; y: number } | null {
    // Clone the position and project it to screen space
    const screenPos = worldPosition.clone();
    screenPos.project(this.camera);

    // screenPos now contains normalized device coordinates (-1 to +1)
    // If z > 1, the point is behind the camera
    if (screenPos.z > 1) {
      return null;
    }

    // Convert from NDC (-1 to +1) to percentage (0 to 100)
    const percentX = ((screenPos.x + 1) / 2) * 100;
    const percentY = ((-screenPos.y + 1) / 2) * 100; // Flip Y back

    return { x: percentX, y: percentY };
  }

  dispose() {
    try {
      if (this.mjvScene) {
        this.mjvScene.delete();
        this.mjvScene = null;
      }
    } catch {
      this.mjvScene = null;
    }

    try {
      this.mjvCamera.delete();
      this.mjvCamera = null as any;
    } catch {
      // Already deleted
    }

    try {
      this.mjvPerturb.delete();
      this.mjvPerturb = null as any;
    } catch {
      // Already deleted
    }

    try {
      this.mjvOption.delete();
      this.mjvOption = null as any;
    } catch {
      // Already deleted
    }

    try {
      if (this.mjData) {
        this.mjData.delete();
        this.mjData = null;
      }
    } catch {
      this.mjData = null;
    }

    try {
      if (this.mjModel) {
        this.mjModel.delete();
        this.mjModel = null;
      }
    } catch {
      this.mjModel = null;
    }

    this.meshes.forEach((mesh) => {
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((material: { dispose: () => void }) => {
          material.dispose();
        });
      } else {
        mesh.material.dispose();
      }
      mesh.geometry.dispose();
    });
    this.bufferGeometryCache.clear();
    this.textureCache.clear();
    this.textureCacheByFilename.clear();

    // Clean up lights
    this.lights.forEach((light) => {
      if (light instanceof THREE.SpotLight) {
        this.scene.remove(light.target);
      }
      this.scene.remove(light);
      if (light instanceof THREE.PointLight || light instanceof THREE.SpotLight) {
        light.dispose();
      }
    });
    this.lights = [];

    this.controls.dispose();
    this.renderer.dispose();

    // Clean up resize observer
    if ((this as any)._resizeObserver) {
      (this as any)._resizeObserver.disconnect();
    }

    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  private listFilesystem(dir: string, prefix: string = '', maxDepth: number = 3): void {
    if (maxDepth <= 0) return;

    const fsAny = this.mujoco.FS as any;
    try {
      const entries = fsAny.readdir(dir);
      for (const entry of entries) {
        if (entry === '.' || entry === '..') continue;

        const fullPath = dir === '/' ? `/${entry}` : `${dir}/${entry}`;
        try {
          const stat = fsAny.stat(fullPath);
          const isDir = (stat.mode & 0o040000) !== 0;

          if (isDir) {
            this.listFilesystem(fullPath, prefix + '  ', maxDepth - 1);
          }
        } catch {
          console.log(`${prefix}❌ ${entry} (stat error)`);
        }
      }
    } catch {
      console.log(`${prefix}❌ Cannot read directory: ${dir}`);
    }
  }

  loadModel(xmlContent: string, xmlPath?: string) {
    // Reset frame count and centering flag for new model
    this.frameCount = 0;
    this.sceneCentered = false;

    const fsAny = this.mujoco.FS as any;

    // If xmlPath is provided and the file exists, use it directly to preserve relative paths
    // Otherwise, write to /working/model.xml
    let pathToLoad: string;
    if (xmlPath && xmlPath.startsWith('/working/')) {
      try {
        // Check if file exists at the provided path
        fsAny.stat(xmlPath);
        pathToLoad = xmlPath;
        console.log(`Using existing XML file at: ${xmlPath}`);
      } catch {
        // File doesn't exist, write the content
        fsAny.writeFile('/working/model.xml', xmlContent);
        pathToLoad = '/working/model.xml';
        console.log('XML file not found at provided path, using /working/model.xml');
      }
    } else {
      // No path provided or not in /working, write to default location
      fsAny.writeFile('/working/model.xml', xmlContent);
      pathToLoad = '/working/model.xml';
      console.log('Writing XML to /working/model.xml');
    }

    console.log(`Loading MuJoCo model from: ${pathToLoad}`);

    // Log filesystem structure for debugging
    console.log('Filesystem structure in /working:');
    this.listFilesystem('/working', '', 3);

    try {
      this.mjModel = this.mujoco.MjModel.mj_loadXML(pathToLoad);

      // Enable sleep optimization (allows MuJoCo to skip simulation for inactive bodies)
      // This provides a ~3.6x speedup in physics (from 4.3ms/step to 1.2ms/step)
      this.mjModel.opt.enableflags |= this.mujoco.mjtEnableBit.mjENBL_SLEEP.value;
      console.log('✓ mjENBL_SLEEP optimization enabled');

      this.mjData = new this.mujoco.MjData(this.mjModel);

      // Log model information for debugging
      console.log('🤖 Model loaded successfully');
      console.log(`   nq (positions): ${this.mjModel.nq}`);
      console.log(`   nv (velocities): ${this.mjModel.nv}`);
      console.log(`   nu (actuators/controls): ${this.mjModel.nu}`);
      console.log(`   nbody (bodies): ${this.mjModel.nbody}`);

      // Log initial control values
      if (this.mjData.ctrl && this.mjData.ctrl.length > 0) {
        console.log(`   ctrl array length: ${this.mjData.ctrl.length}`);
        for (let i = 0; i < Math.min(10, this.mjData.ctrl.length); i++) {
          console.log(`   ctrl[${i}] = ${this.mjData.ctrl[i]}`);
        }
      } else {
        console.log('   ⚠️ No controls (ctrl) found in model!');
      }
    } catch (error) {
      console.error('Failed to load MuJoCo model. Filesystem dump:');
      this.listFilesystem('/working', '', 4);
      throw error;
    }

    // Parse material-texture mapping from XML
    if (xmlPath && xmlPath.startsWith('/working/')) {
      try {
        const fsAny = this.mujoco.FS as any;
        const xmlFromFS = fsAny.readFile(xmlPath, { encoding: 'utf8' });
        if (xmlFromFS && typeof xmlFromFS === 'string') {
          this.parseMaterialTextureMapping(xmlFromFS);
        }
      } catch (e) {
        console.warn('Failed to parse XML for material-texture mapping:', e);
      }
    } else {
      this.parseMaterialTextureMapping(xmlContent);
    }

    // Discover textures in filesystem
    console.log('Discovering texture files...');
    const textures = this.discoverTextureFiles();
    console.log(`Discovered ${textures.length} texture files`);

    this.initScene();
  }

  parseMaterialTextureMapping(xmlContent: string) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xmlContent, 'text/xml');

      const parserError = doc.querySelector('parsererror');
      if (parserError) {
        console.error('XML parsing error:', parserError.textContent);
        return;
      }

      const materials = doc.querySelectorAll('material');
      const textures = doc.querySelectorAll('texture[file]');

      console.log(`Found ${materials.length} materials and ${textures.length} textures in XML`);

      const textureNameToFile = new Map<string, string>();
      textures.forEach((texture) => {
        const name = texture.getAttribute('name');
        const file = texture.getAttribute('file');
        if (name && file) {
          textureNameToFile.set(name, file);
          // console.log(`Texture "${name}" -> "${file}"`);
        }
      });

      let materialIndex = 0;
      materials.forEach((material) => {
        const matName = material.getAttribute('name');
        const texName = material.getAttribute('texture');
        if (matName && texName && textureNameToFile.has(texName)) {
          const textureFile = textureNameToFile.get(texName);
          if (!textureFile) return;

          this.materialNameToTextureMap.set(matName, textureFile);

          try {
            // eslint-disable-next-line camelcase
            const mjOBJ_MATERIAL = 2;
            const matId = this.mjModel
              ? this.mujoco.mj_name2id(this.mjModel, mjOBJ_MATERIAL, matName)
              : -1;
            if (matId >= 0) {
              this.materialToTextureMap.set(matId, textureFile);
              // console.log(`Mapped material "${matName}" (ID ${matId}) to texture: ${textureFile}`);
            } else {
              this.materialToTextureMap.set(materialIndex, textureFile);
              // console.log(`Mapped material "${matName}" (index ${materialIndex}) to texture: ${textureFile}`);
            }
          } catch {
            this.materialToTextureMap.set(materialIndex, textureFile);
            // console.log(`Mapped material "${matName}" (index ${materialIndex}) to texture: ${textureFile}`);
          }
        }
        materialIndex++;
      });

      console.log(`Created ${this.materialToTextureMap.size} material-to-texture mappings`);
    } catch (e) {
      console.error('Error parsing XML for material-texture mapping:', e);
    }
  }

  discoverTextureFiles(): string[] {
    if (this.discoveredTextures.length > 0) {
      return this.discoveredTextures;
    }

    if ((this as any)._textureDiscoveryAttempted) {
      return this.discoveredTextures;
    }

    const isDirectory = (stat: any): boolean => {
      return (stat.mode & 0o040000) !== 0;
    };

    const textures: string[] = [];
    const fsAny = this.mujoco.FS as any;

    try {
      const searchDir = (dir: string, basePath: string = '') => {
        try {
          const files = fsAny.readdir(dir);
          for (const file of files) {
            if (file === '.' || file === '..') continue;

            try {
              const fullPath = `${dir}/${file}`;
              const stat = fsAny.stat(fullPath);

              if (isDirectory(stat)) {
                const newBasePath = basePath ? `${basePath}/${file}` : file;
                searchDir(fullPath, newBasePath);
              } else if (file.endsWith('.png') || file.endsWith('.jpg') || file.endsWith('.jpeg')) {
                const texturePath = basePath ? `${basePath}/${file}` : file;
                textures.push(texturePath);
              }
            } catch {
              // Skip files we can't access
            }
          }
        } catch {
          // Skip directories we can't read
        }
      };

      searchDir('/working', '');

      this.discoveredTextures = textures;
      (this as any)._textureDiscoveryAttempted = true;

      if (textures.length > 0) {
        console.log(`Discovered ${textures.length} texture files`);
      }
    } catch {
      (this as any)._textureDiscoveryAttempted = true;
    }

    return textures;
  }

  getMaterialName(matId: number): string | null {
    if (!this.mjModel) return null;

    let matNameadr: any = (this.mjModel as any).mat_nameadr;
    if (typeof matNameadr === 'function') {
      matNameadr = matNameadr();
    }

    if (!matNameadr || matId < 0 || matId >= matNameadr.length) {
      return null;
    }

    let textAdr: any = (this.mjModel as any).text_adr;
    let textData: any = (this.mjModel as any).text_data;
    if (typeof textAdr === 'function') {
      textAdr = textAdr();
    }
    if (typeof textData === 'function') {
      textData = textData();
    }

    if (!textAdr || !textData) {
      return null;
    }

    const nameAdr = matNameadr[matId];
    if (nameAdr < 0 || nameAdr >= textAdr.length) {
      return null;
    }

    const nameStart = textAdr[nameAdr];
    let nameEnd = nameStart;
    while (nameEnd < textData.length && textData[nameEnd] !== 0) {
      nameEnd++;
    }
    return String.fromCharCode(...textData.slice(nameStart, nameEnd));
  }

  findTextureFileRecursive(texturePath: string, searchDir: string = '/working'): string | null {
    const fileName = texturePath.split('/').pop() || texturePath;
    const fsAny = this.mujoco.FS as any;

    const isDirectory = (stat: any): boolean => {
      return (stat.mode & 0o040000) !== 0;
    };

    const search = (dir: string): string | null => {
      try {
        const entries = fsAny.readdir(dir);
        for (const entry of entries) {
          if (entry === '.' || entry === '..') continue;

          const fullPath = dir === '/' ? `/${entry}` : `${dir}/${entry}`;
          try {
            const stat = fsAny.stat(fullPath);
            if (isDirectory(stat)) {
              const found = search(fullPath);
              if (found) return found;
            } else if (entry === fileName || entry.endsWith(fileName)) {
              return fullPath;
            }
          } catch {
            continue;
          }
        }
      } catch {
        // Can't read directory
      }
      return null;
    };

    return search(searchDir);
  }

  loadTextureByFilename(
    texturePath: string,
    material: THREE.MeshPhongMaterial,
    mesh: THREE.Mesh,
    texId?: number
  ) {
    if (this.textureCacheByFilename.has(texturePath)) {
      const texture = this.textureCacheByFilename.get(texturePath);
      if (texture) {
        this.applyTextureToMesh(mesh, texture, texturePath, true);
        return;
      }
    }

    const fsAny = this.mujoco.FS as any;
    const pathVariations = [
      `/working/${texturePath}`,
      `/working/${texturePath.replace(/^assets\//, '')}`,
      texturePath.startsWith('/') ? texturePath : `/working/${texturePath}`,
      texturePath.startsWith('/working/') ? texturePath : `/working/${texturePath}`,
      `/working/assets/${texturePath}`,
      texturePath.includes('/') ? `/working/${texturePath.split('/').pop()}` : null,
    ].filter((p): p is string => p !== null);

    let textureData: Uint8Array | null = null;
    let usedPath: string | null = null;

    for (const fullPath of pathVariations) {
      try {
        fsAny.stat(fullPath);
        const data = fsAny.readFile(fullPath, { encoding: 'binary' });
        if (data && data.length > 0) {
          textureData = data;
          usedPath = fullPath;
          break;
        }
      } catch {
        continue;
      }
    }

    if (!textureData || !usedPath) {
      const foundPath = this.findTextureFileRecursive(texturePath, '/working');
      if (foundPath) {
        try {
          const data = fsAny.readFile(foundPath, { encoding: 'binary' });
          if (data && data.length > 0) {
            textureData = data;
            usedPath = foundPath;
          }
        } catch {
          // Failed to read
        }
      }
    }

    if (!textureData || !usedPath) {
      return;
    }

    this.createTextureFromData(textureData, texturePath, material, mesh, texId);
  }

  createTextureFromData(
    textureData: Uint8Array,
    texturePath: string,
    material: THREE.MeshPhongMaterial,
    mesh: THREE.Mesh,
    texId?: number
  ) {
    let mimeType = 'image/png';
    if (texturePath.endsWith('.jpg') || texturePath.endsWith('.jpeg')) {
      mimeType = 'image/jpeg';
    }

    const buffer = textureData.buffer.slice(
      textureData.byteOffset,
      textureData.byteOffset + textureData.byteLength
    );
    const arrayBuffer = buffer instanceof ArrayBuffer ? buffer : new Uint8Array(textureData).buffer;
    const blob = new Blob([arrayBuffer], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const loader = new THREE.TextureLoader();

    loader.load(
      url,
      (texture) => {
        texture.flipY = false;
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        this.textureCacheByFilename.set(texturePath, texture);
        if (texId !== undefined) {
          this.textureCache.set(texId, texture);
        }

        this.applyTextureToMesh(mesh, texture, texturePath, true);

        // Apply to all meshes with same material
        for (const [matId, texPath] of this.materialIdToTexturePath.entries()) {
          if (texPath === texturePath) {
            const meshesWithMaterial = this.materialIdToMeshes.get(matId);
            meshesWithMaterial?.forEach((m) => {
              this.applyTextureToMesh(m, texture, texturePath, true);
            });
          }
        }

        material.map = texture;
        if (material.userData['originalRgba'] && material.userData['originalRgba'].length >= 3) {
          const rgba = material.userData['originalRgba'] as number[];
          material.color.setRGB(rgba[0] ?? 1, rgba[1] ?? 1, rgba[2] ?? 1);
        } else {
          material.color.setRGB(1, 1, 1);
        }
        material.needsUpdate = true;

        URL.revokeObjectURL(url);
      },
      undefined,
      (error) => {
        console.error(`Failed to load texture: ${texturePath}`, error);
        URL.revokeObjectURL(url);
      }
    );
  }

  applyTextureToMesh(
    mesh: THREE.Mesh,
    texture: THREE.Texture,
    texturePath: string,
    silent: boolean = false,
    rgba?: number[]
  ): void {
    const currentMeshMaterial = mesh.material;

    if (Array.isArray(currentMeshMaterial)) {
      currentMeshMaterial.forEach((mat) => {
        if (mat instanceof THREE.MeshPhongMaterial) {
          mat.map = texture;
          let colorR = 1;
          let colorG = 1;
          let colorB = 1;
          if (rgba && rgba.length >= 3) {
            colorR = rgba[0] ?? 1;
            colorG = rgba[1] ?? 1;
            colorB = rgba[2] ?? 1;
          } else if (mat.userData['originalRgba'] && mat.userData['originalRgba'].length >= 3) {
            const origRgba = mat.userData['originalRgba'] as number[];
            colorR = origRgba[0] ?? 1;
            colorG = origRgba[1] ?? 1;
            colorB = origRgba[2] ?? 1;
          }
          mat.color.setRGB(colorR, colorG, colorB);
          mat.needsUpdate = true;
        }
      });
      if (!silent) {
        console.log(`Texture applied to mesh: ${texturePath}`);
      }
    } else if (currentMeshMaterial instanceof THREE.MeshPhongMaterial) {
      currentMeshMaterial.map = texture;
      let colorR = 1;
      let colorG = 1;
      let colorB = 1;
      if (rgba && rgba.length >= 3) {
        colorR = rgba[0] ?? 1;
        colorG = rgba[1] ?? 1;
        colorB = rgba[2] ?? 1;
      } else if (
        currentMeshMaterial.userData['originalRgba'] &&
        currentMeshMaterial.userData['originalRgba'].length >= 3
      ) {
        colorR = currentMeshMaterial.userData['originalRgba'][0] ?? 1;
        colorG = currentMeshMaterial.userData['originalRgba'][1] ?? 1;
        colorB = currentMeshMaterial.userData['originalRgba'][2] ?? 1;
      }
      currentMeshMaterial.color.setRGB(colorR, colorG, colorB);
      currentMeshMaterial.needsUpdate = true;
      if (!silent) {
        console.log(`Texture applied to mesh: ${texturePath}`);
      }
    }
  }

  tryLoadTextureForMaterial(
    matId: number,
    material: THREE.MeshPhongMaterial,
    mesh: THREE.Mesh
  ): boolean {
    if (this.materialToTextureMap.has(matId)) {
      const textureFilePath = this.materialToTextureMap.get(matId);
      if (!textureFilePath) return false;

      this.materialIdToTexturePath.set(matId, textureFilePath);

      if (this.textureCacheByFilename.has(textureFilePath)) {
        const texture = this.textureCacheByFilename.get(textureFilePath);
        if (!texture) return false;

        const meshesWithMaterial = this.materialIdToMeshes.get(matId);
        if (meshesWithMaterial) {
          meshesWithMaterial.forEach((m) => {
            this.applyTextureToMesh(m, texture, textureFilePath, true);
          });
        } else {
          this.applyTextureToMesh(mesh, texture, textureFilePath, true);
        }
        return true;
      } else {
        this.loadTextureByFilename(textureFilePath, material, mesh);
        return true;
      }
    }

    return false;
  }

  pause() {
    this.paused = !this.paused;
  }

  isPaused(): boolean {
    return this.paused;
  }

  reset() {
    if (this.mjModel && this.mjData) {
      console.log('Resetting model and data');

      this.mujoco.mj_resetData(this.mjModel, this.mjData);
      this.mujoco.mj_forward(this.mjModel, this.mjData);

      this.clearScene();
      this.initScene();
    }
  }

  toggleContacts() {
    const index = this.mujoco.mjtVisFlag.mjVIS_CONTACTPOINT.value;
    const flags = this.mjvOption.flags as boolean[];
    const value = flags[index] ?? false;
    flags[index] = !value;

    this.clearScene();
    this.initScene();
  }

  areContactsVisible(): boolean {
    const index = this.mujoco.mjtVisFlag.mjVIS_CONTACTPOINT.value;
    const flags = this.mjvOption.flags as boolean[];
    return flags[index] ?? false;
  }

  setCameraMode(mode: CameraMode, skipCentering: boolean = false): void {
    console.log(`📹 setCameraMode called: mode=${mode}, skipCentering=${skipCentering}`);
    this.cameraMode = mode;

    // Set control parameters based on mode
    switch (mode) {
      case 'top-down':
        // Lock camera in top-down mode - no user interaction
        this.controls.enableRotate = false;
        this.controls.enablePan = false;
        this.controls.enableZoom = false;
        this.controls.minDistance = CAMERA_CONFIG.topDown.minZoomDistance;
        this.controls.maxDistance = CAMERA_CONFIG.topDown.maxZoomDistance;
        break;

      case '3rd-person':
        // Enable full free motion controls for positioning
        this.controls.enableRotate = true;
        this.controls.enablePan = true;
        this.controls.enableZoom = true;
        this.controls.minDistance = 0.1; // Allow very close positioning
        this.controls.maxDistance = 100; // Allow far positioning
        this.controls.screenSpacePanning = true; // Better panning control
        break;

      case '1st-person':
        this.controls.enableRotate = true;
        this.controls.enablePan = true; // Enable free move controls
        this.controls.enableZoom = true;
        this.controls.minDistance = CAMERA_CONFIG.firstPerson.minDistance;
        this.controls.maxDistance = CAMERA_CONFIG.firstPerson.maxDistance;
        break;
    }

    // Center camera when switching to top-down or 3rd-person mode (unless explicitly skipped)
    if (!skipCentering) {
      if (mode === 'top-down') {
        if (this.meshes.length > 0) {
          this.centerCameraOnScene('top-down');
        } else {
          // Fallback to default top-down position if no meshes yet
          this.camera.position.set(0, 0, 10);
          this.controls.target.set(0, 0, 0);
          this.controls.update();
        }
      } else if (mode === '3rd-person') {
        // Check if we have action JSON with camera config
        if (this.robotInitParameters?.initialCameraPos && this.robotInitParameters?.cameraTarget) {
          // Use camera position from action JSON config (pre-calculated)
          const camPos = this.robotInitParameters.initialCameraPos;
          const camTarget = this.robotInitParameters.cameraTarget;

          console.log(
            `✅ Using PRE-CALCULATED camera from actionJson: pos=(${camPos.x.toFixed(2)}, ${camPos.y.toFixed(2)}, ${camPos.z.toFixed(2)}), target=(${camTarget.x.toFixed(2)}, ${camTarget.y.toFixed(2)}, ${camTarget.z.toFixed(2)})`
          );

          this.camera.position.set(camPos.x, camPos.y, camPos.z);
          this.controls.target.set(camTarget.x, camTarget.y, camTarget.z);
          this.camera.up.set(0, 0, 1);
          this.controls.update();
        } else {
          // No pre-calculated positions - calculate dynamically now
          console.log(`⚙️  No pre-calculated camera - calculating dynamically now`);

          if (this.robotInitParameters && this.mjData && this.mjModel) {
            // Calculate camera position dynamically based on robot parameters
            this.calculateDynamicCameraPosition();
          } else {
            console.warn(
              '⚠️  Cannot calculate dynamic camera - missing robot parameters or model/data'
            );
            // Fall back to auto-centering
            if (this.meshes.length > 0) {
              this.centerCameraOnScene('3rd-person');
            }
          }
        }
      }
    }
    // For 1st-person mode, don't auto-center (use existing position or set explicitly)
  }

  getCameraMode(): CameraMode {
    return this.cameraMode;
  }

  /**
   * Gets the current action JSON data.
   */
  getActionJson(): any {
    return this.actionJson;
  }

  /**
   * Gets the robot initialization parameters from the current action.
   */
  getRobotInitParameters(): any {
    return this.robotInitParameters;
  }

  /**
   * Gets the 2D screen position of a MuJoCo body by name.
   * Projects the 3D world position of the body through the camera to get screen coordinates.
   * @param name - Name of the MuJoCo body (e.g., object name from action JSON)
   * @param normalized - If true, returns coordinates normalized to [0, 1], otherwise in pixels
   * @returns Object with x, y coordinates (normalized or in pixels depending on flag)
   */
  getMujocoBodyScreenPos(name: string, normalized: boolean = true): { x: number; y: number } {
    if (!this.mjModel || !this.mjData) {
      console.warn('getMujocoBodyScreenPos called but model/data not initialized');
      return { x: 0, y: 0 };
    }

    // Get body ID from name
    const targetBodyId = this.mujoco.mj_name2id(
      this.mjModel,
      this.mujoco.mjtObj.mjOBJ_BODY.value,
      name
    );

    if (targetBodyId < 0) {
      console.warn(`Body with name "${name}" not found in MuJoCo model`);
      return { x: 0, y: 0 };
    }

    // Get body's 3D position from xpos array
    const targetXposIdx = targetBodyId * 3;
    const pos = new THREE.Vector3(
      this.mjData.xpos[targetXposIdx + 0],
      this.mjData.xpos[targetXposIdx + 1],
      this.mjData.xpos[targetXposIdx + 2]
    );

    // Get viewport dimensions
    const { width, height } = this.renderer.domElement.getBoundingClientRect();

    // Project to screen coordinates
    const screenPos = worldPosToScreenPos(pos, this.camera, width, height);

    // Normalize if requested
    if (normalized) {
      return {
        x: screenPos.x / width,
        y: screenPos.y / height,
      };
    }

    return screenPos;
  }

  /**
   * Sets the trajectory data for robot demonstrations.
   * @param trajectory - Trajectory data containing robot poses and actions
   */
  setTrajectoryData(trajectory: TrajectoryData | null): void {
    this.trajectoryData = trajectory;
    this.currentTrajectoryFrame = 0;
    if (trajectory) {
      console.log(
        `Loaded trajectory: ${trajectory.commanded_action.length} frames, dt=${trajectory.policy_dt_ms}ms`
      );

      // Apply initial Joint Pos.
      this.applyInitialRobotPose(trajectory.init_qpos);
    }
  }

  setActiveActionIndex(activeIndex: number): void {
    if (
      this.allActionsJson &&
      this.allActionsJson.actions &&
      activeIndex < this.allActionsJson.actions.length
    ) {
      this.activeActionIndex = activeIndex;
      this.actionJson = this.allActionsJson.actions[activeIndex];

      const robotInitParameters = this.actionJson.robotParams || {};

      // Extract robot position and rotation from robot_base_pose
      if (this.actionJson.robot_base_pose && this.actionJson.robot_base_pose.length > 0) {
        const initPos = this.actionJson.robot_base_pose[0];

        if (initPos.length === 7) {
          robotInitParameters.robotPos = {
            x: initPos[0],
            y: initPos[1],
            z: initPos[2],
          };

          robotInitParameters.robotRot = {
            w: initPos[3],
            x: initPos[4],
            y: initPos[5],
            z: initPos[6],
          };
        } else {
          throw new Error(
            'robot_base_pose[0] must be an array of length 7 (position xyz + rotation wxyz)'
          );
        }
      } else {
        throw new Error('robot_base_pose in actionJson must have at least one position');
      }

      this.robotInitParameters = robotInitParameters;
      console.log('Set MuJoCo actions with robot parameters:', robotInitParameters);

      // Position the robot immediately if we have position data
      if (robotInitParameters.robotPos && robotInitParameters.robotRot) {
        this.setRobotBasePosition(robotInitParameters.robotPos, robotInitParameters.robotRot);
      }
    } else {
      throw new Error(
        `Either 'this.allActionsJson' is null: ${this.allActionsJson === null} or allActionsJson.actions.length is smaller than 'activeIndex: ${activeIndex}`
      );
    }
  }

  getAvailableObjects(): any {
    return this.allActionsJson && this.allActionsJson.objects ? this.allActionsJson.objects : null;
  }

  getOverlayPosition(objectIndex: number) {
    if (this.allActionsJson?.objects && objectIndex < this.allActionsJson.objects.length) {
      let m = this.getMujocoBodyScreenPos(this.allActionsJson.objects[objectIndex].name, true);
      m.x = m.x * 100;
      m.y = m.y * 100;
      console.log(` --------------- getOverlayPosition call `);
      console.log(m);
      return m;
    } else {
      return null;
    }
  }

  getObjectOverlays(): any {
    // return this.objectOverlayPositions;
    if (this.allActionsJson?.objects) {
      return this.allActionsJson.objects.map((x: any) => {
        return {
          name: x.name,
          screenPosition: this.getMujocoBodyScreenPos(x.name, true),
        };
      });
    } else {
      return [];
    }
  }

  /**
   * Sets the action JSON data for the current scene action.
   * Extracts robot initialization parameters from the action including
   * robot position, rotation, and camera positioning.
   * @param actionJson - Action data containing robot pose, camera params, and object info
   */
  setMujocoActions(actionsJson: any, activeIndex: number): void {
    this.allActionsJson = actionsJson;
    this.setActiveActionIndex(activeIndex);

    // const robotInitParameters = this.actionJson.robotParams || {};

    // // Extract robot position and rotation from robot_base_pose
    // if (this.actionJson.robot_base_pose && this.actionJson.robot_base_pose.length > 0) {
    //   const initPos = this.actionJson.robot_base_pose[0];

    //   if (initPos.length === 7) {
    //     robotInitParameters.robotPos = {
    //       x: initPos[0],
    //       y: initPos[1],
    //       z: initPos[2],
    //     };

    //     robotInitParameters.robotRot = {
    //       w: initPos[3],
    //       x: initPos[4],
    //       y: initPos[5],
    //       z: initPos[6],
    //     };
    //   } else {
    //     throw new Error(
    //       'robot_base_pose[0] must be an array of length 7 (position xyz + rotation wxyz)'
    //     );
    //   }
    // } else {
    //   throw new Error('robot_base_pose in actionJson must have at least one position');
    // }

    // this.robotInitParameters = robotInitParameters;
    // console.log('Set MuJoCo actions with robot parameters:', robotInitParameters);

    // // Position the robot immediately if we have position data
    // if (robotInitParameters.robotPos && robotInitParameters.robotRot) {
    //   this.setRobotBasePosition(robotInitParameters.robotPos, robotInitParameters.robotRot);
    // }

    // Re-center camera on robot if we're in top-down mode
    // This ensures the camera is properly positioned after actions load, even if auto-centering
    // happened at frame 3 before the actions JSON was available
    if (this.cameraMode === 'top-down' && this.robotInitParameters?.robotPos) {
      console.log('📸 Re-centering camera on robot after actions loaded');
      this.centerCameraOnScene('top-down');
    }

    this.objectOverlayPositions = actionsJson.objects.map((x: any) => {
      return {
        name: x.name,
        screenPosition: this.getMujocoBodyScreenPos(x.name, true),
      };
    });

    // Don't apply camera positioning here for 3rd-person - it will be applied automatically by setCameraMode()
    // when switching to 3rd-person mode. This ensures we stay in top-down view initially.
  }

  /**
   * Toggles action playback on/off.
   * When forceStop is true, resets to the beginning and starts playing.
   * @param forceStop - If true, resets playback to start
   */
  playActions(forceStop: boolean = false): void {
    if (forceStop) {
      this.actionPlaybackIndex = 0;
      this.playing = true;
    }

    if (!this.playing) {
      this.playing = true;
      this.secondsSinceLastAction = 0.0;
    } else {
      this.playing = false;
      this.secondsSinceLastAction = 0.0;
    }

    console.log(`Action playback ${this.playing ? 'started' : 'paused'}`);
  }

  /**
   * Resets the action playback to the beginning without resetting the entire scene.
   * Resets robot to initial Joint Pos.s and applies first action.
   */
  resetPlayback(): void {
    this.actionPlaybackIndex = 0;
    this.secondsSinceLastAction = 0.0;
    this.playActions(true);
    this.setRobotQpositions(this.actionJson['init_qpos']);

    const controlsInit = this.getFirstValidAction();
    this.setControls(controlsInit);
  }

  /**
   * Sets the robot's base position and orientation in the world using mocap.
   * The robot base must be set up as a mocap body in the MuJoCo model.
   * @param position - {x, y, z} position in world coordinates
   * @param quaternion - {w, x, y, z} orientation quaternion
   */
  private setRobotBasePosition(
    position: { x: number; y: number; z: number },
    quaternion: { w: number; x: number; y: number; z: number }
  ): void {
    if (!this.mjData || !this.mjModel) {
      console.warn('setRobotBasePosition called but model/data not initialized');
      return;
    }

    const mjModel = this.mjModel;
    const mjData = this.mjData;

    // Find the robot base body
    const robotBodyId = this.mujoco.mj_name2id(
      mjModel,
      this.mujoco.mjtObj.mjOBJ_BODY.value,
      'robot_0/base'
    );

    if (robotBodyId < 0) {
      console.warn('Robot base body "robot_0/base" not found');
      return;
    }

    // Get the mocap ID for this body (robot must be set up as mocap body)
    const mocapId = mjModel.body_mocapid[robotBodyId];

    if (mocapId < 0) {
      console.warn('Robot base body is not a mocap body - cannot position directly');
      return;
    }

    // Set mocap position (xyz)
    const posIdx = mocapId * 3;
    mjData.mocap_pos[posIdx + 0] = position.x;
    mjData.mocap_pos[posIdx + 1] = position.y;
    mjData.mocap_pos[posIdx + 2] = position.z;

    // Set mocap quaternion (wxyz)
    const quatIdx = mocapId * 4;
    mjData.mocap_quat[quatIdx + 0] = quaternion.w;
    mjData.mocap_quat[quatIdx + 1] = quaternion.x;
    mjData.mocap_quat[quatIdx + 2] = quaternion.y;
    mjData.mocap_quat[quatIdx + 3] = quaternion.z;

    // Update simulation
    this.mujoco.mj_forward(mjModel, mjData);

    console.log(
      `Set robot base position: (${position.x.toFixed(2)}, ${position.y.toFixed(2)}, ${position.z.toFixed(2)}) quat: (${quaternion.w.toFixed(2)}, ${quaternion.x.toFixed(2)}, ${quaternion.y.toFixed(2)}, ${quaternion.z.toFixed(2)})`
    );
  }

  /**
   * Calculates camera position dynamically based on robotParams.
   * Matches the algorithm from app_clean.ts (lines 824-982).
   * This is used when the actions JSON doesn't have pre-calculated camera positions.
   */
  private calculateDynamicCameraPosition(): void {
    if (!this.mjData || !this.mjModel || !this.robotInitParameters) {
      return;
    }

    // Check if we need to calculate (only if no pre-calculated positions exist)
    const hasPreCalculated =
      this.robotInitParameters.initialCameraPos && this.robotInitParameters.cameraTarget;

    // Get parameters from robotInitParameters
    const cameraTargetJoint = this.robotInitParameters.cameraTargetJoint || 'robot_0/fr3_joint4';
    const bodyDirectionForCamera = this.robotInitParameters.bodyDirectionForCamera || 'forward';
    const cameraDistance = this.robotInitParameters.cameraDistance || 1.6;
    const cameraElevation = this.robotInitParameters.cameraElevation || 0.0;
    const rotateOffsetYDegrees = this.robotInitParameters.cameraRotateYOffsetDegrees || 60;
    const rotateOffsetZDegrees = this.robotInitParameters.cameraRotateZOffsetDegrees || 0;

    console.log('🎥 calculateDynamicCameraPosition - Parameters:', {
      robotPos: this.robotInitParameters.robotPos,
      robotRot: this.robotInitParameters.robotRot,
      cameraTargetJoint,
      bodyDirectionForCamera,
      cameraDistance,
      cameraElevation,
      rotateOffsetYDegrees,
      rotateOffsetZDegrees,
      hasPreCalculated,
    });

    const mjModel = this.mjModel;
    const mjData = this.mjData;

    // Find the target joint
    const targetJointId = this.mujoco.mj_name2id(
      mjModel,
      this.mujoco.mjtObj.mjOBJ_JOINT.value,
      cameraTargetJoint
    );

    if (targetJointId < 0) {
      console.warn(`Target joint ${cameraTargetJoint} not found`);
      return;
    }

    // Get joint anchor position (where the camera should look at)
    const jointAnchorIdx = targetJointId * 3;
    const targetPos = new THREE.Vector3(
      mjData.xanchor[jointAnchorIdx + 0],
      mjData.xanchor[jointAnchorIdx + 1],
      mjData.xanchor[jointAnchorIdx + 2]
    );

    // Log robot base position for comparison
    const robotBodyId = this.mujoco.mj_name2id(
      mjModel,
      this.mujoco.mjtObj.mjOBJ_BODY.value,
      'robot_0/base'
    );
    const robotBaseIdx = robotBodyId * 3;
    const robotBasePos = new THREE.Vector3(
      mjData.xpos[robotBaseIdx + 0],
      mjData.xpos[robotBaseIdx + 1],
      mjData.xpos[robotBaseIdx + 2]
    );

    console.log('🤖 Robot base actual position:', {
      x: robotBasePos.x.toFixed(2),
      y: robotBasePos.y.toFixed(2),
      z: robotBasePos.z.toFixed(2),
    });
    console.log(`🎯 Target joint (${cameraTargetJoint}) position:`, {
      x: targetPos.x.toFixed(2),
      y: targetPos.y.toFixed(2),
      z: targetPos.z.toFixed(2),
    });

    // Get the body that this joint belongs to
    const jointBodyId = mjModel.jnt_bodyid[targetJointId];

    // Get the body's forward direction based on the specified direction
    const forwardDir = this.getBodyDirection(jointBodyId, bodyDirectionForCamera);
    const forwardNorm = forwardDir.clone().normalize();

    console.log('➡️  Body forward direction (raw):', {
      x: forwardNorm.x.toFixed(2),
      y: forwardNorm.y.toFixed(2),
      z: forwardNorm.z.toFixed(2),
    });

    // Project forward direction onto horizontal plane (XY plane) to prevent camera from going underground
    // This ensures the camera stays at a reasonable height even if the robot is tilted
    const forwardHorizontal = new THREE.Vector3(forwardNorm.x, forwardNorm.y, 0).normalize();

    console.log('➡️  Body forward direction (horizontal projection):', {
      x: forwardHorizontal.x.toFixed(2),
      y: forwardHorizontal.y.toFixed(2),
      z: forwardHorizontal.z.toFixed(2),
    });

    // Calculate camera offset: horizontal forward direction * distance + elevation
    const upVector = new THREE.Vector3(0, 0, 1);
    const cameraOffset = forwardHorizontal
      .clone()
      .multiplyScalar(cameraDistance)
      .add(upVector.clone().multiplyScalar(cameraElevation));

    console.log('📐 Camera offset before rotation:', {
      x: cameraOffset.x.toFixed(2),
      y: cameraOffset.y.toFixed(2),
      z: cameraOffset.z.toFixed(2),
    });

    // Apply rotation offsets around up axis (Z) and Y axis
    const rotationAxis = new THREE.Vector3(0, 0, 1); // Z is up
    const rotationQuaternion = new THREE.Quaternion();
    const rotationQuaternionZ = new THREE.Quaternion();
    rotationQuaternion.setFromAxisAngle(rotationAxis, (Math.PI / 180) * rotateOffsetYDegrees);
    rotationQuaternionZ.setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      (Math.PI / 180) * rotateOffsetZDegrees
    );

    rotationQuaternion.multiply(rotationQuaternionZ);
    cameraOffset.applyQuaternion(rotationQuaternion);

    console.log('📐 Camera offset after rotation:', {
      x: cameraOffset.x.toFixed(2),
      y: cameraOffset.y.toFixed(2),
      z: cameraOffset.z.toFixed(2),
    });

    // Calculate final camera position
    const cameraPos = targetPos.clone().add(cameraOffset);

    // Safety check: Ensure camera Z is never below a minimum height (0.3m above floor)
    // This prevents the camera from ending up underground or inside objects
    const MIN_CAMERA_Z = 0.3;
    if (cameraPos.z < MIN_CAMERA_Z) {
      console.warn(
        `⚠️  Camera Z position (${cameraPos.z.toFixed(2)}) is below minimum (${MIN_CAMERA_Z}). Adjusting to minimum height.`
      );
      cameraPos.z = MIN_CAMERA_Z;
    }

    // Apply camera position
    this.camera.position.set(cameraPos.x, cameraPos.y, cameraPos.z);
    this.camera.up.set(0, 0, 1);
    this.camera.lookAt(targetPos);

    // IMPORTANT: Must set OrbitControls target
    this.controls.target.copy(targetPos);
    this.controls.update();

    console.log(
      `Calculated dynamic camera position: pos=(${cameraPos.x.toFixed(2)}, ${cameraPos.y.toFixed(2)}, ${cameraPos.z.toFixed(2)}), target=(${targetPos.x.toFixed(2)}, ${targetPos.y.toFixed(2)}, ${targetPos.z.toFixed(2)})`
    );
  }

  /**
   * Gets the direction vector for a body based on the specified direction.
   * @param bodyId - MuJoCo body ID
   * @param direction - Direction string ('forward', 'back', 'left', 'right')
   * @returns Direction vector
   */
  private getBodyDirection(bodyId: number, direction: string): THREE.Vector3 {
    if (!this.mjData || !this.mjModel) {
      return new THREE.Vector3(1, 0, 0); // Default forward
    }

    const mjData = this.mjData;

    // Get body rotation matrix (3x3)
    const matIdx = bodyId * 9;
    const mat = new THREE.Matrix4();
    mat.set(
      mjData.xmat[matIdx + 0],
      mjData.xmat[matIdx + 3],
      mjData.xmat[matIdx + 6],
      0,
      mjData.xmat[matIdx + 1],
      mjData.xmat[matIdx + 4],
      mjData.xmat[matIdx + 7],
      0,
      mjData.xmat[matIdx + 2],
      mjData.xmat[matIdx + 5],
      mjData.xmat[matIdx + 8],
      0,
      0,
      0,
      0,
      1
    );

    // Define direction vectors in body frame
    let dirVec: THREE.Vector3;
    switch (direction) {
      case 'forward':
        dirVec = new THREE.Vector3(1, 0, 0);
        break;
      case 'back':
        dirVec = new THREE.Vector3(-1, 0, 0);
        break;
      case 'left':
        dirVec = new THREE.Vector3(0, 1, 0);
        break;
      case 'right':
        dirVec = new THREE.Vector3(0, -1, 0);
        break;
      default:
        dirVec = new THREE.Vector3(1, 0, 0);
    }

    // Transform direction by body rotation
    dirVec.applyMatrix4(mat);
    return dirVec;
  }

  /**
   * Sets robot Joint Pos.s from qpos data.
   * Handles both named joints (object format) and gripper positions.
   * @param qPositions - Object containing joint names and values, plus optional gripper array
   */
  private setRobotQpositions(qPositions: any): void {
    if (!this.mjData || !this.mjModel) {
      console.warn('setRobotQpositions called but model/data not initialized');
      return;
    }

    const gripperNames = [
      'robot_0/gripper/left_driver_joint',
      'robot_0/gripper/right_driver_joint',
    ];

    const mjModel = this.mjModel;
    const mjData = this.mjData;

    // Set named Joint Pos.s
    Object.entries(qPositions).forEach(([key, value]) => {
      if (key === 'gripper') {
        // Handle gripper separately
        const gripperValues = value as number[];
        gripperNames.forEach((gripperName, idx) => {
          if (idx < gripperValues.length) {
            const jointId = this.mujoco.mj_name2id(
              mjModel,
              this.mujoco.mjtObj.mjOBJ_JOINT.value,
              gripperName
            );
            if (jointId >= 0) {
              const qposadr = mjModel.jnt_qposadr[jointId];
              mjData.qpos[qposadr] = gripperValues[idx];
            }
          }
        });
      } else {
        // Regular named joint
        const jointId = this.mujoco.mj_name2id(mjModel, this.mujoco.mjtObj.mjOBJ_JOINT.value, key);
        if (jointId >= 0) {
          const qposadr = mjModel.jnt_qposadr[jointId];
          mjData.qpos[qposadr] = value as number;
        }
      }
    });

    // Forward kinematics to update visualization
    this.mujoco.mj_forward(mjModel, mjData);
  }

  /**
   * Sets control values for the robot actuators from an action.
   * @param action - Action object containing arm and gripper control values
   */
  private setControls(action: any): void {
    if (!this.mjData || !action) {
      return;
    }

    // Set arm controls (first 7 actuators)
    if (action.arm) {
      for (let i = 0; i < Math.min(action.arm.length, 7); i++) {
        this.mjData.ctrl[i] = action.arm[i];
      }
    }

    // Set gripper controls (actuators 7 and 8)
    if (action.gripper) {
      for (let i = 0; i < Math.min(action.gripper.length, 2); i++) {
        this.mjData.ctrl[7 + i] = action.gripper[i];
      }
    }
  }

  /**
   * Gets the first valid (non-empty) action from the commanded_action array.
   * @returns First valid action or null if none found
   */
  private getFirstValidAction(): any {
    if (!this.actionJson || !this.actionJson.commanded_action) {
      return null;
    }

    let index = 0;
    let firstAction = null;

    while (
      (!firstAction || Object.keys(firstAction).length === 0) &&
      index < this.actionJson.commanded_action.length
    ) {
      firstAction = this.actionJson.commanded_action[index];
      index++;
    }

    return firstAction;
  }

  /**
   * Applies initial robot Joint Pos.s from trajectory data.
   * Sets both qpos (for immediate positioning) and ctrl (for control targets).
   * @param initQpos - Initial Joint Pos.s containing arm and gripper
   */
  private applyInitialRobotPose(initQpos: { arm: number[]; gripper: number[] }): void {
    if (!this.mjData || !this.mjModel) {
      return;
    }

    try {
      const nu = this.mjModel.nu; // Number of actuators
      const nq = this.mjModel.nq; // Number of positions

      // Set control targets (ctrl) - these drive the actuators
      for (let i = 0; i < Math.min(initQpos.arm.length, nu); i++) {
        this.mjData.ctrl[i] = initQpos.arm[i];
      }

      // Also set initial qpos directly for immediate positioning
      // Assuming robot joints start after world/base joints
      let jointIdx = 0;
      for (let i = 0; i < nq && jointIdx < initQpos.arm.length; i++) {
        if (i >= 7 && i < 7 + initQpos.arm.length) {
          this.mjData.qpos[i] = initQpos.arm[jointIdx];
          jointIdx++;
        }
      }

      // Apply gripper
      if (initQpos.gripper.length > 0) {
        const gripperCtrlStart = initQpos.arm.length;
        for (let i = 0; i < initQpos.gripper.length && gripperCtrlStart + i < nu; i++) {
          this.mjData.ctrl[gripperCtrlStart + i] = initQpos.gripper[i];
        }

        const gripperQposStart = 7 + initQpos.arm.length;
        for (let i = 0; i < initQpos.gripper.length && gripperQposStart + i < nq; i++) {
          this.mjData.qpos[gripperQposStart + i] = initQpos.gripper[i];
        }
      }

      // Forward kinematics to update the visualization
      this.mujoco.mj_forward(this.mjModel, this.mjData);
      console.log('🤖 Applied initial robot pose (qpos + ctrl)');
    } catch (error) {
      console.warn('Failed to apply initial robot pose:', error);
    }
  }

  /**
   * Gets the current trajectory data if available.
   */
  getTrajectoryData(): TrajectoryData | null {
    return this.trajectoryData;
  }

  /**
   * Starts playing the trajectory animation.
   * Automatically steps through frames and updates camera position.
   */
  playTrajectory(): void {
    if (!this.trajectoryData || this.trajectoryPlaying) {
      return;
    }

    this.trajectoryPlaying = true;
    this.lastTrajectoryUpdateTime = performance.now();

    console.log('▶️ Starting trajectory playback');

    // Start the animation loop
    this.trajectoryAnimationId = requestAnimationFrame(this.updateTrajectoryFrame.bind(this));
  }

  /**
   * Stops playing the trajectory animation.
   */
  stopTrajectory(): void {
    if (!this.trajectoryPlaying) {
      return;
    }

    this.trajectoryPlaying = false;

    if (this.trajectoryAnimationId !== null) {
      cancelAnimationFrame(this.trajectoryAnimationId);
      this.trajectoryAnimationId = null;
    }

    console.log('⏸️ Stopped trajectory playback at frame', this.currentTrajectoryFrame);
  }

  /**
   * Extends the robot upwards by setting control[5] to the extended position.
   * This should be called when switching to 3rd-person mode.
   */
  extendRobotUpward(): void {
    if (!this.mjData || !this.mjModel) {
      return;
    }

    // Set ctrl[5] to extended position (matching the animation's max value)
    if (this.mjData.ctrl.length > 5) {
      this.mjData.ctrl[5] = 3.0; // Extended position
      console.log('🤖 Extended robot upward (ctrl[5] = 3.0)');

      // Forward kinematics to update the visualization immediately
      this.mujoco.mj_forward(this.mjModel, this.mjData);
    }
  }

  /**
   * Starts the repeating robot action animation.
   * This makes the robot move back and forth.
   * @param onComplete - Optional callback when animation completes
   */
  startRepeatAction(onComplete?: () => void): void {
    if (this.repeatActionPlaying) {
      return;
    }

    this.repeatActionPlaying = true;
    this.repeatActionCallback = onComplete || null;

    console.log('▶️ Starting repeat action animation');
  }

  /**
   * Stops the repeating robot action animation.
   */
  stopRepeatAction(): void {
    if (!this.repeatActionPlaying) {
      return;
    }

    this.repeatActionPlaying = false;
    console.log('⏸️ Stopped repeat action animation');

    // Call completion callback if it exists
    if (this.repeatActionCallback) {
      this.repeatActionCallback();
      this.repeatActionCallback = null;
    }
  }

  /**
   * Checks if repeat action is currently playing.
   */
  isRepeatActionPlaying(): boolean {
    return this.repeatActionPlaying;
  }

  /**
   * Resets trajectory to the first frame.
   */
  resetTrajectory(): void {
    this.stopTrajectory();
    this.currentTrajectoryFrame = 0;

    if (this.cameraMode === '1st-person' && this.trajectoryData) {
      this.positionCameraFromRobotPose(0);
    }

    console.log('⏮️ Reset trajectory to frame 0');
  }

  /**
   * Internal update loop for trajectory animation.
   * Called by requestAnimationFrame.
   */
  private updateTrajectoryFrame(): void {
    if (!this.trajectoryPlaying || !this.trajectoryData) {
      return;
    }

    const now = performance.now();
    const elapsed = now - this.lastTrajectoryUpdateTime;
    const frameDuration = this.trajectoryData.policy_dt_ms;

    // Check if it's time to advance to the next frame
    if (elapsed >= frameDuration) {
      this.currentTrajectoryFrame++;
      this.lastTrajectoryUpdateTime = now;

      // Check if we've reached the end
      if (this.currentTrajectoryFrame >= this.trajectoryData.robot_base_pose.length) {
        console.log('🏁 Trajectory playback complete');
        this.stopTrajectory();
        return;
      }

      // Update camera only if in 1st-person mode
      // Note: User can still control camera with mouse/keyboard during playback
      if (this.cameraMode === '1st-person') {
        this.positionCameraFromRobotPose(this.currentTrajectoryFrame);
      }

      // Apply robot Joint Pos.s from trajectory data
      this.applyRobotJointPositions(this.currentTrajectoryFrame);
    }

    // Continue the animation loop
    this.trajectoryAnimationId = requestAnimationFrame(this.updateTrajectoryFrame.bind(this));
  }

  /**
   * Gets the current trajectory frame index.
   */
  getCurrentTrajectoryFrame(): number {
    return this.currentTrajectoryFrame;
  }

  /**
   * Checks if trajectory is currently playing.
   */
  isTrajectoryPlaying(): boolean {
    return this.trajectoryPlaying;
  }

  /**
   * Gets the robot's base pose at a specific frame index.
   * @param frameIndex - Index of the frame (default: current frame)
   * @returns Robot pose [x, y, z, qw, qx, qy, qz] or null if not available
   */
  getRobotPoseAtFrame(frameIndex?: number): number[] | null {
    if (!this.trajectoryData) return null;

    const idx = frameIndex !== undefined ? frameIndex : this.currentTrajectoryFrame;
    if (idx < 0 || idx >= this.trajectoryData.robot_base_pose.length) {
      return null;
    }

    return this.trajectoryData.robot_base_pose[idx] ?? null;
  }

  /**
   * Applies robot Joint Pos.s from trajectory data to the MuJoCo simulation.
   * Uses MuJoCo's control system (ctrl) to drive the robot.
   * @param frameIndex - Index of the frame to apply
   */
  private applyRobotJointPositions(frameIndex: number): void {
    if (!this.trajectoryData || !this.mjData || !this.mjModel) {
      return;
    }

    // Get commanded action for this frame
    const action = this.trajectoryData.commanded_action[frameIndex];
    if (!action || !action.arm) {
      return;
    }

    try {
      const armJoints = action.arm;

      // Apply to MuJoCo control inputs (ctrl) instead of directly to qpos
      // The actuators will then move the robot to these positions
      const nu = this.mjModel.nu; // Number of actuators

      // Copy arm joint commands to ctrl array
      for (let i = 0; i < Math.min(armJoints.length, nu); i++) {
        this.mjData.ctrl[i] = armJoints[i];
      }

      // Apply gripper if available
      if (action.gripper && action.gripper.length > 0) {
        const gripperStartIdx = armJoints.length;
        for (let i = 0; i < action.gripper.length && gripperStartIdx + i < nu; i++) {
          this.mjData.ctrl[gripperStartIdx + i] = action.gripper[i];
        }
      }
    } catch (error) {
      console.warn('Failed to apply robot Joint Pos.s:', error);
    }
  }

  /**
   * Positions the camera to match the robot's viewpoint from trajectory data.
   * Uses the robot's base pose and adds eye height to simulate the robot's camera.
   * @param frameIndex - Optional frame index (default: current frame)
   */
  positionCameraFromRobotPose(frameIndex?: number): boolean {
    const pose = this.getRobotPoseAtFrame(frameIndex);
    if (!pose || pose.length < 7) {
      return false;
    }

    // Extract position and quaternion from pose
    // Format: [x, y, z, qw, qx, qy, qz]
    const [x, y, z, qw, qx, qy, qz] = pose;

    // Robot camera is typically at eye height above the base
    const robotCameraHeight = CAMERA_CONFIG.firstPerson.eyeHeight;

    // Position camera at robot's location plus camera height
    this.camera.position.set(x ?? 0, y ?? 0, (z ?? 0) + robotCameraHeight);

    // Convert quaternion to Euler angles to set camera orientation
    // THREE.js Quaternion can be used to set camera rotation
    const quat = new THREE.Quaternion(qx, qy, qz, qw);

    // Apply robot orientation to camera
    // Note: MuJoCo and THREE.js may have different coordinate conventions
    this.camera.quaternion.copy(quat);

    // Calculate forward direction from quaternion for controls target
    const forward = new THREE.Vector3(0, 1, 0); // Forward direction in camera local space
    forward.applyQuaternion(quat);
    forward.normalize();

    // Set controls target 2 meters ahead in the look direction
    const lookDistance = 2.0;
    this.controls.target.set(
      this.camera.position.x + forward.x * lookDistance,
      this.camera.position.y + forward.y * lookDistance,
      this.camera.position.z + forward.z * lookDistance
    );

    this.controls.update();

    console.log(
      `🤖 Robot camera at frame ${frameIndex ?? this.currentTrajectoryFrame}: pos=(${x?.toFixed(2)}, ${y?.toFixed(2)}, ${z?.toFixed(2)}) forward=(${forward.x.toFixed(2)}, ${forward.y.toFixed(2)}, ${forward.z.toFixed(2)})`
    );
    console.log(
      `🤖 Final camera position: (${this.camera.position.x.toFixed(2)}, ${this.camera.position.y.toFixed(2)}, ${this.camera.position.z.toFixed(2)})`
    );

    return true;
  }

  /**
   * Centers the camera on the scene geometry based on the specified camera mode.
   * For scenes with robots and actions JSON, centers on the robot in top-down mode.
   * Otherwise calculates bounding box and positions camera appropriately for the mode.
   *
   * @param mode - The camera mode to center for (defaults to current mode)
   */
  centerCameraOnScene(mode?: CameraMode): void {
    if (this.meshes.length === 0) {
      return;
    }

    const targetMode = mode || this.cameraMode;

    // For top-down mode with robot and actions JSON, center on robot instead of entire scene
    if (targetMode === 'top-down' && this.robotInitParameters?.robotPos) {
      const robotPos = this.robotInitParameters.robotPos;
      const center = new THREE.Vector3(robotPos.x, robotPos.y, robotPos.z);

      console.log(
        `Centering camera on robot for ${targetMode} mode: robot pos=(${center.x.toFixed(2)}, ${center.y.toFixed(2)}, ${center.z.toFixed(2)})`
      );

      // Set controls target to robot position
      this.controls.target.copy(center);

      // Position camera above robot
      const distance = CAMERA_CONFIG.topDown.minDistance * 2; // Use reasonable distance for robot view
      this.camera.position.set(center.x, center.y, center.z + distance);
      this.camera.up.set(0, 0, 1);
      this.controls.update();

      console.log(
        `   Top-down camera positioned at: (${this.camera.position.x.toFixed(2)}, ${this.camera.position.y.toFixed(2)}, ${this.camera.position.z.toFixed(2)})`
      );
      this.update();
      this.render();
      let m = this.getObjectOverlays();

      console.log(`---------- centerCameraOnScene`);
      console.log(m);
      return;
    }

    // Calculate bounding box of all meshes (fallback for non-robot scenes or other modes)
    const bbox = new THREE.Box3();
    this.meshes.forEach((mesh) => {
      bbox.expandByObject(mesh);
    });

    // Get center point and size
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    console.log(
      `Centering camera for ${targetMode} mode: center=(${center.x.toFixed(2)}, ${center.y.toFixed(2)}, ${center.z.toFixed(2)}), size=(${size.x.toFixed(2)}, ${size.y.toFixed(2)}, ${size.z.toFixed(2)})`
    );

    // Set controls target to scene center (for all modes except 1st-person)
    if (targetMode !== '1st-person') {
      this.controls.target.copy(center);
    }

    // Position camera based on mode
    switch (targetMode) {
      case 'top-down': {
        const distance = Math.max(
          maxDim * CAMERA_CONFIG.topDown.distanceMultiplier,
          CAMERA_CONFIG.topDown.minDistance
        );

        console.log(
          `   Top-down distance: ${distance.toFixed(2)} (aspect ratio: ${this.camera.aspect.toFixed(3)})`
        );

        this.camera.position.set(center.x, center.y, center.z + distance);
        break;
      }

      case '3rd-person': {
        // Position camera 1 meter above floor, offset from center
        const cameraHeight = 1.0; // 1 meter above floor
        const horizontalOffset: number = CAMERA_CONFIG.thirdPerson.horizontalOffset;

        // Find the floor level by raycasting
        const downRaycaster = new THREE.Raycaster();
        const rayStart = new THREE.Vector3(center.x, center.y, bbox.max.z + 10);
        const rayDirection = new THREE.Vector3(0, 0, -1);
        downRaycaster.set(rayStart, rayDirection);
        const downIntersects = downRaycaster.intersectObjects(this.meshes, false);
        let floorZ = bbox.min.z;
        if (downIntersects.length > 0 && downIntersects[0]) {
          floorZ = downIntersects[0].point.z;
        }

        // Position camera behind the center point at 1 meter height
        this.camera.position.set(
          center.x - horizontalOffset, // Back from center
          center.y, // Same Y (left-right)
          floorZ + cameraHeight // 1 meter above floor
        );

        // Look at the center at the same height
        this.controls.target.set(center.x, center.y, floorZ + cameraHeight);

        console.log(
          `3rd-person position: (${this.camera.position.x.toFixed(2)}, ${this.camera.position.y.toFixed(2)}, ${this.camera.position.z.toFixed(2)})`
        );
        break;
      }

      case '1st-person': {
        // Find the actual floor by raycasting downward from above the center
        const eyeHeight = CAMERA_CONFIG.firstPerson.eyeHeight;
        const ceilingClearance = CAMERA_CONFIG.firstPerson.ceilingClearance;
        const downRaycaster = new THREE.Raycaster();

        // Start well above the scene and raycast down
        const rayStart = new THREE.Vector3(center.x, center.y, bbox.max.z + 10);
        const rayDirection = new THREE.Vector3(0, 0, -1); // Point straight down
        downRaycaster.set(rayStart, rayDirection);

        const downIntersects = downRaycaster.intersectObjects(this.meshes, false);

        // Use the floor Z if found, otherwise use bounding box bottom
        const floorZ =
          downIntersects.length > 0 && downIntersects[0] ? downIntersects[0].point.z : bbox.min.z;

        // Calculate desired camera Z and clamp to scene bounds
        let cameraZ = floorZ + eyeHeight;
        const maxZ = bbox.max.z - ceilingClearance;
        const minZ = bbox.min.z + eyeHeight;
        cameraZ = Math.max(minZ, Math.min(maxZ, cameraZ));

        // Position camera at clamped height
        this.camera.position.set(center.x, center.y - 2, cameraZ);
        this.controls.target.set(center.x, center.y, cameraZ);

        console.log(
          `1st-person: Floor z=${floorZ.toFixed(2)}, camera z=${cameraZ.toFixed(2)} (clamped: ${minZ.toFixed(2)} - ${maxZ.toFixed(2)})`
        );
        break;
      }
    }

    // Update controls
    this.controls.update();
  }

  /**
   * Creates lights from the MuJoCo model definition.
   * Reads light data from the model and creates corresponding Three.js lights.
   */
  private createLightsFromModel() {
    if (!this.mjModel) {
      return;
    }

    const nlight = this.mjModel.nlight;
    if (nlight === 0) {
      // If no lights in model, add default lights
      console.log('No lights defined in model, using default lighting');
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
      this.scene.add(ambientLight);
      this.lights.push(ambientLight);

      const pointLight = new THREE.PointLight(0xffffff, 0.4);
      pointLight.position.set(0, 0, 2);
      pointLight.castShadow = true;
      pointLight.shadow.mapSize.set(2048, 2048);
      this.scene.add(pointLight);
      this.lights.push(pointLight);
      return;
    }

    // Access light arrays from model
    const lightType: any = (this.mjModel as any).light_type;
    const lightPos: any = (this.mjModel as any).light_pos;
    const lightDir: any = (this.mjModel as any).light_dir;
    const lightActive: any = (this.mjModel as any).light_active;
    const lightDiffuse: any = (this.mjModel as any).light_diffuse;
    const lightCastshadow: any = (this.mjModel as any).light_castshadow;

    // MuJoCo light type constants
    // eslint-disable-next-line camelcase
    const mjLIGHT_DIRECTIONAL = 1;

    console.log(`Creating ${nlight} lights from MuJoCo model`);

    // Create lights from model
    for (let i = 0; i < nlight; i++) {
      // Skip inactive lights
      if (lightActive && lightActive[i] === 0) {
        continue;
      }

      const type = lightType[i];
      const pos = [lightPos[i * 3], lightPos[i * 3 + 1], lightPos[i * 3 + 2]];
      const dir = [lightDir[i * 3], lightDir[i * 3 + 1], lightDir[i * 3 + 2]];
      const diffuse = lightDiffuse
        ? [lightDiffuse[i * 3], lightDiffuse[i * 3 + 1], lightDiffuse[i * 3 + 2]]
        : [1, 1, 1];

      // Use a higher intensity for better visibility (MuJoCo's lighting is different from Three.js)
      const intensity = 5.4;

      const castShadow = lightCastshadow && lightCastshadow[i] !== 0;

      const color = new THREE.Color(diffuse[0], diffuse[1], diffuse[2]);

      // eslint-disable-next-line camelcase
      if (type === mjLIGHT_DIRECTIONAL) {
        // Directional light (most common in MuJoCo scenes)
        const dirLight = new THREE.DirectionalLight(color, intensity);
        dirLight.position.set(pos[0], pos[1], pos[2]);
        dirLight.target.position.set(
          Number(pos[0]) + Number(dir[0]),
          Number(pos[1]) + Number(dir[1]),
          Number(pos[2]) + Number(dir[2])
        );

        dirLight.castShadow = castShadow;
        if (castShadow) {
          dirLight.shadow.mapSize.set(2048, 2048);
        }

        this.scene.add(dirLight);
        this.scene.add(dirLight.target);
        this.lights.push(dirLight);

        console.log(
          `Created directional light ${i}: pos=[${pos.join(', ')}], dir=[${dir.join(', ')}], intensity=${intensity}`
        );
      } else {
        console.warn(`Unsupported light type ${type} for light ${i}, skipping`);
      }
    }

    // Always add a subtle ambient light for better scene visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.2);
    this.scene.add(ambientLight);
    this.lights.push(ambientLight);
  }

  private initScene() {
    console.log('🔧 initScene() called');

    // Only create mjvScene if model exists
    if (!this.mjModel) {
      return;
    }

    // Clean up old mjvScene if it exists
    if (this.mjvScene) {
      this.mjvScene.delete();
      this.mjvScene = null;
    }

    this.mjvScene = new this.mujoco.MjvScene(this.mjModel, this.maxGeoms);

    // Only add lights if they don't exist
    if (this.lights.length === 0) {
      this.createLightsFromModel();
    }

    // Apply robot initialization from actionJson if available
    if (this.actionJson && this.robotInitParameters) {
      console.log('🔧 Applying robot initialization from actionJson');
      console.log('   robotInitParameters:', this.robotInitParameters);
      // Reset action playback state
      this.playing = false;
      this.actionPlaybackIndex = 0;

      // Set robot base position from robotInitParameters
      if (this.robotInitParameters.robotPos && this.robotInitParameters.robotRot) {
        this.setRobotBasePosition(
          this.robotInitParameters.robotPos,
          this.robotInitParameters.robotRot
        );
      }

      // Set initial robot Joint Pos.s
      if (this.actionJson.init_qpos) {
        this.setRobotQpositions(this.actionJson.init_qpos);
      }

      // Apply first valid action controls
      const firstAction = this.getFirstValidAction();
      if (firstAction) {
        this.setControls(firstAction);
      }

      // Calculate camera position dynamically from robotParams (matching app_clean.ts)
      // This happens BEFORE checking for pre-calculated positions, so pre-calculated can override
      this.calculateDynamicCameraPosition();

      // Override with pre-calculated camera position if available
      if (this.robotInitParameters.initialCameraPos && this.robotInitParameters.cameraTarget) {
        const camPos = this.robotInitParameters.initialCameraPos;
        const camTarget = this.robotInitParameters.cameraTarget;

        this.camera.position.set(camPos.x, camPos.y, camPos.z);
        this.controls.target.set(camTarget.x, camTarget.y, camTarget.z);
        this.camera.up.set(0, 0, 1);
        this.controls.update();

        console.log(
          `Applied pre-calculated camera from actionJson: pos=(${camPos.x.toFixed(2)}, ${camPos.y.toFixed(2)}, ${camPos.z.toFixed(2)}), target=(${camTarget.x.toFixed(2)}, ${camTarget.y.toFixed(2)}, ${camTarget.z.toFixed(2)})`
        );
      }

      // Force an update to apply the changes
      if (this.mjData) {
        this.mujoco.mj_forward(this.mjModel, this.mjData);
      }
    }
  }

  private clearScene() {
    // Dispose meshes and their resources
    this.meshes.forEach((mesh) => {
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach((material: { dispose: () => void }) => {
          material.dispose();
        });
      } else {
        mesh.material.dispose();
      }
      mesh.geometry.dispose();
    });

    this.bufferGeometryCache.clear();
    this.meshes.length = 0;

    // Remove all children except lights
    const childrenToRemove: THREE.Object3D[] = [];
    this.scene.children.forEach((child) => {
      if (
        !this.lights.includes(child as THREE.Light) &&
        child !== (this.lights[2] as any)?.target
      ) {
        childrenToRemove.push(child);
      }
    });
    childrenToRemove.forEach((child) => {
      this.scene.remove(child);
    });

    // Clean up mjvScene
    if (this.mjvScene) {
      this.mjvScene.delete();
      this.mjvScene = null;
    }
  }

  /**
   * Swaps the current model with a new one without recreating the app.
   * This is more efficient than disposing and recreating the entire app.
   */
  swapModel(xmlContent: string, xmlPath?: string) {
    console.log('Swapping model...');

    // Pause the simulation during swap
    const wasPaused = this.paused;
    this.paused = true;

    // Clean up old model data
    if (this.mjData) {
      this.mjData.delete();
      this.mjData = null;
    }
    if (this.mjModel) {
      this.mjModel.delete();
      this.mjModel = null;
    }

    // Clear old meshes and scene
    this.clearScene();

    // Dispose all textures before clearing maps
    this.textureCache.forEach((texture) => {
      texture.dispose();
    });
    this.textureCacheByFilename.forEach((texture) => {
      texture.dispose();
    });

    // Clear texture and material mappings
    this.textureCache.clear();
    this.textureCacheByFilename.clear();
    this.materialToTextureMap.clear();
    this.materialNameToTextureMap.clear();
    this.materialIdToTexturePath.clear();
    this.materialIdToMeshes.clear();
    this.discoveredTextures = [];

    // Reset centering flag and frame count so the new scene gets centered
    this.sceneCentered = false;
    this.frameCount = 0;

    // Clear trajectory data when swapping scenes
    this.trajectoryData = null;
    this.currentTrajectoryFrame = 0;

    // Load new model
    this.loadModel(xmlContent, xmlPath);

    // Restore pause state
    this.paused = wasPaused;

    console.log('Model swap complete');
  }

  /**
   * Shows the canvas by making it visible
   */
  showCanvas() {
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.visibility = 'visible';
  }

  /**
   * Hides the canvas without disposing resources
   */
  hideCanvas() {
    this.renderer.domElement.style.display = 'none';
    this.renderer.domElement.style.visibility = 'hidden';
  }

  private getBufferGeometry(mjvGeom: MjvGeom): [boolean, THREE.BufferGeometry] {
    if (!(mjvGeom instanceof this.mujoco.MjvGeom)) {
      throw new Error('mjvGeom is not an instance of mujoco.MjvGeom');
    }

    const key = JSON.stringify([mjvGeom.type, mjvGeom.size, mjvGeom.dataid]);
    const found = this.bufferGeometryCache.get(key);
    if (found) {
      return [false, found];
    }

    const size = mjvGeom.size as number[];
    let geom: THREE.BufferGeometry;

    if (mjvGeom.type === this.mujoco.mjtGeom.mjGEOM_PLANE.value) {
      geom = new THREE.PlaneGeometry(2 * (size[0] ?? 10000), 2 * (size[1] ?? 10000));
      const uv = geom.getAttribute('uv');
      for (let i = 0; i < uv.count; ++i) {
        uv.setY(i, 1 - uv.getY(i));
      }
    } else if (mjvGeom.type === this.mujoco.mjtGeom.mjGEOM_SPHERE.value) {
      geom = new THREE.SphereGeometry(size[0] ?? 1);
    } else if (mjvGeom.type === this.mujoco.mjtGeom.mjGEOM_CAPSULE.value) {
      geom = new CapsuleGeometry(size[0] ?? 1, 2 * (size[2] ?? 1), 32, 16);
      geom.rotateX(0.5 * Math.PI);
    } else if (mjvGeom.type === this.mujoco.mjtGeom.mjGEOM_BOX.value) {
      geom = new THREE.BoxGeometry(2 * (size[0] ?? 1), 2 * (size[1] ?? 1), 2 * (size[2] ?? 1));
    } else if (mjvGeom.type === this.mujoco.mjtGeom.mjGEOM_CYLINDER.value) {
      geom = new THREE.CylinderGeometry(size[0] ?? 1, size[1] ?? 1, 2 * (size[2] ?? 1), 32);
      geom.rotateX(0.5 * Math.PI);
    } else if (mjvGeom.type === this.mujoco.mjtGeom.mjGEOM_ELLIPSOID.value) {
      geom = new THREE.SphereGeometry(1);
      geom.scale(size[0] ?? 1, size[1] ?? 1, size[2] ?? 1);
    } else if (mjvGeom.type === this.mujoco.mjtGeom.mjGEOM_MESH.value) {
      geom = this.createMeshGeometry(mjvGeom);
    } else {
      console.log('Unsupported geom type: ', mjvGeom.type);
      geom = new THREE.BufferGeometry();
    }

    this.bufferGeometryCache.set(key, geom);
    return [true, geom];
  }

  private createMeshGeometry(mjvGeom: MjvGeom): THREE.BufferGeometry {
    if (!this.mjModel) {
      return new THREE.BufferGeometry();
    }

    const meshId = Math.floor(mjvGeom.dataid / 2);

    if (meshId < 0 || meshId >= this.mjModel.nmesh) {
      console.warn(`Invalid mesh ID: ${meshId}`);
      return new THREE.BufferGeometry();
    }

    try {
      let meshVertadr: any = (this.mjModel as any).mesh_vertadr;
      let meshVertnum: any = (this.mjModel as any).mesh_vertnum;
      let meshFaceadr: any = (this.mjModel as any).mesh_faceadr;
      let meshFacenum: any = (this.mjModel as any).mesh_facenum;
      let meshVert: any = (this.mjModel as any).mesh_vert;
      let meshFace: any = (this.mjModel as any).mesh_face;

      if (typeof meshVertadr === 'function') {
        meshVertadr = meshVertadr();
        meshVertnum = meshVertnum();
        meshFaceadr = meshFaceadr();
        meshFacenum = meshFacenum();
        meshVert = meshVert();
        meshFace = meshFace();
      }

      const vertStart = meshVertadr[meshId];
      const nvert = meshVertnum[meshId];
      const faceStart = meshFaceadr[meshId];
      const nface = meshFacenum[meshId];

      let positions = new Float32Array(nvert * 3);
      for (let i = 0; i < nvert; i++) {
        const idx = (Number(vertStart) + i) * 3;
        positions[i * 3] = meshVert[idx];
        positions[i * 3 + 1] = meshVert[idx + 1];
        positions[i * 3 + 2] = meshVert[idx + 2];
      }

      let indices = new Uint32Array(nface * 3);
      for (let i = 0; i < nface; i++) {
        const idx = (Number(faceStart) + i) * 3;
        indices[i * 3] = meshFace[idx];
        indices[i * 3 + 1] = meshFace[idx + 1];
        indices[i * 3 + 2] = meshFace[idx + 2];
      }

      let normals = new Float32Array(nvert * 3);
      for (let i = 0; i < nvert * 3; i++) {
        normals[i] = 0;
      }

      // Compute normals from geometry
      for (let i = 0; i < nface; i++) {
        const idx = i * 3;
        const v0 = indices[idx];
        const v1 = indices[idx + 1];
        const v2 = indices[idx + 2];

        // Skip invalid triangles
        if (v0 === undefined || v1 === undefined || v2 === undefined) continue;

        const p0x = positions[v0 * 3] ?? 0;
        const p0y = positions[v0 * 3 + 1] ?? 0;
        const p0z = positions[v0 * 3 + 2] ?? 0;
        const p1x = positions[v1 * 3] ?? 0;
        const p1y = positions[v1 * 3 + 1] ?? 0;
        const p1z = positions[v1 * 3 + 2] ?? 0;
        const p2x = positions[v2 * 3] ?? 0;
        const p2y = positions[v2 * 3 + 1] ?? 0;
        const p2z = positions[v2 * 3 + 2] ?? 0;

        const v10x = p1x - p0x;
        const v10y = p1y - p0y;
        const v10z = p1z - p0z;
        const v20x = p2x - p0x;
        const v20y = p2y - p0y;
        const v20z = p2z - p0z;

        let nx = v10y * v20z - v10z * v20y;
        let ny = v10z * v20x - v10x * v20z;
        let nz = v10x * v20y - v10y * v20x;

        const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
        if (len > 0) {
          nx /= len;
          ny /= len;
          nz /= len;
        }

        normals[v0 * 3] = (normals[v0 * 3] ?? 0) + nx;
        normals[v0 * 3 + 1] = (normals[v0 * 3 + 1] ?? 0) + ny;
        normals[v0 * 3 + 2] = (normals[v0 * 3 + 2] ?? 0) + nz;
        normals[v1 * 3] = (normals[v1 * 3] ?? 0) + nx;
        normals[v1 * 3 + 1] = (normals[v1 * 3 + 1] ?? 0) + ny;
        normals[v1 * 3 + 2] = (normals[v1 * 3 + 2] ?? 0) + nz;
        normals[v2 * 3] = (normals[v2 * 3] ?? 0) + nx;
        normals[v2 * 3 + 1] = (normals[v2 * 3 + 1] ?? 0) + ny;
        normals[v2 * 3 + 2] = (normals[v2 * 3 + 2] ?? 0) + nz;
      }

      // Normalize normals
      for (let i = 0; i < nvert; i++) {
        const nx = normals[i * 3] ?? 0;
        const ny = normals[i * 3 + 1] ?? 0;
        const nz = normals[i * 3 + 2] ?? 0;
        const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
        if (len > 0) {
          normals[i * 3] = nx / len;
          normals[i * 3 + 1] = ny / len;
          normals[i * 3 + 2] = nz / len;
        }
      }

      // Try to load UV coordinates
      let uvs: Float32Array | null = null;
      try {
        let meshTexcoord: any = (this.mjModel as any).mesh_texcoord;
        let meshTexcoordadr: any = (this.mjModel as any).mesh_texcoordadr;
        let meshTexcoordnum: any = (this.mjModel as any).mesh_texcoordnum;
        let meshFacetexcoord: any = (this.mjModel as any).mesh_facetexcoord;

        if (typeof meshTexcoord === 'function') {
          meshTexcoord = meshTexcoord();
          meshTexcoordadr = meshTexcoordadr();
          meshTexcoordnum = meshTexcoordnum();
          meshFacetexcoord = meshFacetexcoord();
        }

        if (meshTexcoord && meshTexcoordadr && meshTexcoordnum && meshFacetexcoord) {
          const texcoordStart = meshTexcoordadr[meshId];
          const ntexcoord = meshTexcoordnum[meshId];
          if (ntexcoord > 0 && texcoordStart >= 0) {
            const vertexUvMap = new Map<string, number>();
            const newPositions: number[] = [];
            const newNormals: number[] = [];
            const newUvs: number[] = [];
            const newIndices: number[] = [];
            let newVertexIndex = 0;

            for (let i = 0; i < nface; i++) {
              const faceIdx = (Number(faceStart) + i) * 3;
              const texIdx = (Number(faceStart) + i) * 3;

              const v0 = meshFace[faceIdx];
              const v1 = meshFace[faceIdx + 1];
              const v2 = meshFace[faceIdx + 2];

              const t0 = meshFacetexcoord[texIdx];
              const t1 = meshFacetexcoord[texIdx + 1];
              const t2 = meshFacetexcoord[texIdx + 2];

              const getOrCreateVertex = (vertexIdx: number, texCoordIdx: number): number => {
                const key = `${vertexIdx}-${texCoordIdx}`;
                const existing = vertexUvMap.get(key);
                if (existing !== undefined) {
                  return existing;
                }

                const newIdx = newVertexIndex++;
                vertexUvMap.set(key, newIdx);

                newPositions.push(positions[vertexIdx * 3] ?? 0);
                newPositions.push(positions[vertexIdx * 3 + 1] ?? 0);
                newPositions.push(positions[vertexIdx * 3 + 2] ?? 0);

                newNormals.push(normals[vertexIdx * 3] ?? 0);
                newNormals.push(normals[vertexIdx * 3 + 1] ?? 0);
                newNormals.push(normals[vertexIdx * 3 + 2] ?? 0);

                if (texCoordIdx >= 0 && texCoordIdx < ntexcoord) {
                  const texIdx0 = (Number(texcoordStart) + texCoordIdx) * 2;
                  newUvs.push(meshTexcoord[texIdx0]);
                  newUvs.push(meshTexcoord[texIdx0 + 1]);
                } else {
                  newUvs.push(0, 0);
                }

                return newIdx;
              };

              const nv0 = getOrCreateVertex(v0, t0);
              const nv1 = getOrCreateVertex(v1, t1);
              const nv2 = getOrCreateVertex(v2, t2);

              newIndices.push(nv0, nv1, nv2);
            }

            positions = new Float32Array(newPositions);
            normals = new Float32Array(newNormals);
            uvs = new Float32Array(newUvs);
            indices = new Uint32Array(newIndices);
          }
        }
      } catch (e) {
        console.debug('Texture coordinates not available:', e);
      }

      const geom = new THREE.BufferGeometry();
      geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geom.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
      if (uvs) {
        geom.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
      }
      geom.setIndex(new THREE.BufferAttribute(indices, 1));
      geom.computeBoundingSphere();

      return geom;
    } catch (e) {
      console.error('Failed to load mesh geometry:', e);
      return new THREE.BufferGeometry();
    }
  }

  // update() {
  //   // console.log("--------- new update")
  //   if (!this.mjModel || !this.mjData) {
  //     return;
  //   }

  //   // let actionPeriodSeconds = this.actionJson["policy_dt_ms"]/1000;
  //   const actionPeriodSeconds = (this.actionJson?.policy_dt_ms || 200) / 1000.0;

  //   this.controls.update();
  //   // const eps = 0.00008;
  //   // if (Math.abs(app.controls._sphericalDelta.theta) > eps && Math.abs(app.controls._sphericalDelta.theta) > eps) {
  //   //   // console.log(`----- theta ${app.controls._sphericalDelta.theta} phi ${app.controls._sphericalDelta.theta}`);
  //   // }

  //   // TODO logic to detect when movement has stopped when damping is enabled

  //   if (!this.paused) {
  //     let sim_start = this.mjData.time;
  //     let last_update_time = sim_start;

  //     while (this.mjData.time - sim_start < 1. / 60.) {
  //     // while (this.mjData.time - sim_start < 1. / 60.) {
  //       let deltaTimeSeconds = this.mjData.time - last_update_time
  //       // console.log(`------- delta ${deltaTime}`);

  //       // let deltaTime = this.mjData.time - sim_start;

  //       // console.log(deltaTime);

  //       if (this.playing && this.actionJson && this.actionJson["commanded_action"]) {

  //         if (this.secondsSinceLastAction >= actionPeriodSeconds) {

  //           if (this.actionPlaybackIndex < this.actionJson["commanded_action"].length) {
  //             let action = this.actionJson["commanded_action"][this.actionPlaybackIndex];
  //             this.actionPlaybackIndex++;
  //             if (action && Object.keys(action).length > 0) {
  //               this.setControls(action);
  //               this.secondsSinceLastAction = 0.0
  //             }

  //           }
  //         }
  //         else {
  //           this.secondsSinceLastAction += deltaTimeSeconds;
  //         }
  //       }

  //       // this.actionJson
  //       // app.mjData.ctrl[6] += 0.0001;
  //       // app.mjData.ctrl[7] += 0.1;
  //       //   if (this.updateCtrlDir > 0 && app.mjData.ctrl[5] >= 3.0) {
  //       //   this.updateCtrlDir = -1.0;
  //       //   }
  //       //   else if (this.updateCtrlDir < 0 && app.mjData.ctrl[5] < 0.01) {
  //       //   this.updateCtrlDir = 1.0;
  //       //   }
  //       //   app.mjData.ctrl[5] += this.updateCtrlDir * 0.001;

  //         last_update_time = this.mjData.time;
  //         this.mujoco.mj_step(this.mjModel, this.mjData);

  //     }
  //   }
  //   if (!this.mjvScene) return;

  //   this.mujoco.mjv_updateScene(
  //       this.mjModel, this.mjData, this.mjvOption, this.mjvPerturb,
  //       this.mjvCamera, this.mujoco.mjtCatBit.mjCAT_ALL.value, this.mjvScene);

  //   const geoms = this.mjvScene.geoms;
  //   for (let i = 0; i < geoms.size(); i++) {
  //     const mjvGeom = geoms.get(i);
  //     if (!mjvGeom) continue;

  //     let mesh: THREE.Mesh | undefined;
  //     if (i < this.meshes.length) {
  //       mesh = this.meshes[i];
  //       if (!mesh) continue;
  //     } else {
  //       // Create new mesh if it doesn't exist
  //       const [, geom] = this.getBufferGeometry(mjvGeom);
  //       const material = new THREE.MeshPhongMaterial();
  //       material.color.setRGB(mjvGeom.rgba[0], mjvGeom.rgba[1], mjvGeom.rgba[2]);
  //       material.opacity = mjvGeom.rgba[3];
  //       material.transparent = mjvGeom.rgba[3] < 1.0;
  //       material.userData['originalRgba'] = [
  //         mjvGeom.rgba[0],
  //         mjvGeom.rgba[1],
  //         mjvGeom.rgba[2],
  //         mjvGeom.rgba[3],
  //       ];

  //       mesh = new THREE.Mesh(geom, material);
  //       mesh.castShadow = true;
  //       mesh.receiveShadow = true;
  //       this.meshes.push(mesh);
  //       this.scene.add(mesh);
  //     }

  //     // Update mesh transform
  //     mesh.matrixAutoUpdate = false;
  //     const sz = 1;
  //     mesh.matrix.set(
  //       mjvGeom.mat[0], mjvGeom.mat[1], mjvGeom.mat[2] * sz, mjvGeom.pos[0],
  //       mjvGeom.mat[3], mjvGeom.mat[4], mjvGeom.mat[5] * sz, mjvGeom.pos[1],
  //       mjvGeom.mat[6], mjvGeom.mat[7], mjvGeom.mat[8] * sz, mjvGeom.pos[2],
  //       0, 0, 0, 1);
  //     mesh.matrixWorldNeedsUpdate = true;

  //     mjvGeom.delete();
  //   }

  //    geoms.delete();
  // }

  update() {
    if (!this.mjModel || !this.mjData) {
      return;
    }

    const frameStart = performance.now();
    this.frameCount++;

    const controlsStart = performance.now();
    this.controls.update();
    const controlsTime = performance.now() - controlsStart;

    // Debug: Log camera mode and ctrl status once per second
    const currentSecond = Math.floor(this.mjData.time);
    if (currentSecond > 0 && currentSecond !== (this as any)._lastLoggedSecond) {
      (this as any)._lastLoggedSecond = currentSecond;
      const ctrlInfo = this.mjData.ctrl ? `length=${this.mjData.ctrl.length}` : 'NULL';
      console.log(`⏰ Update: mode=${this.cameraMode}, paused=${this.paused}, ctrl=${ctrlInfo}`);
    }

    const physicsStart = performance.now();
    let physicsSteps = 0;

    if (!this.paused) {
      const simStart = this.mjData.time;
      while (this.mjData.time - simStart < 1.0 / 60.0) {
        physicsSteps++;
        // OLD REPEATING ACTION CODE - DISABLED
        // This was just for demo/debug - now we use the proper action playback system
        // The back-and-forth movement was from this old code
        /*
        const timeElapsed = this.mjData.time;
        if (
          this.cameraMode === '3rd-person' &&
          this.mjData.ctrl &&
          this.mjData.ctrl.length > 6 &&
          timeElapsed > 0.5
        ) {
          // Old animation code that made robot move back and forth
          // ... (commented out)
        }
        */

        // Handle action playback from actionJson
        if (this.playing && this.actionJson && this.actionJson.commanded_action) {
          const actionPeriodSeconds = (this.actionJson.policy_dt_ms || 200) / 1000.0;
          const deltaTimeSeconds = 1.0 / 60.0; // Assuming 60 FPS

          if (this.secondsSinceLastAction >= actionPeriodSeconds) {
            if (this.actionPlaybackIndex < this.actionJson.commanded_action.length) {
              const action = this.actionJson.commanded_action[this.actionPlaybackIndex];
              this.actionPlaybackIndex++;

              if (action && Object.keys(action).length > 0) {
                this.setControls(action);
                this.secondsSinceLastAction = 0.0;
              }
            }
          } else {
            this.secondsSinceLastAction += deltaTimeSeconds;
          }
        }

        this.mujoco.mj_step(this.mjModel, this.mjData);
      }
    }
    const physicsTime = performance.now() - physicsStart;

    if (!this.mjvScene) return;

    const sceneUpdateStart = performance.now();
    this.mujoco.mjv_updateScene(
      this.mjModel,
      this.mjData,
      this.mjvOption,
      this.mjvPerturb,
      this.mjvCamera,
      this.mujoco.mjtCatBit.mjCAT_ALL.value,
      this.mjvScene
    );
    const sceneUpdateTime = performance.now() - sceneUpdateStart;

    const meshUpdateStart = performance.now();
    const geoms = this.mjvScene.geoms;
    for (let i = 0; i < geoms.size(); i++) {
      const mjvGeom = geoms.get(i);
      if (!mjvGeom) continue;

      let mesh: THREE.Mesh;
      const existingMesh = this.meshes[i];
      if (existingMesh) {
        // Mesh already exists - just update its transform matrix (like app_clean.ts)
        mesh = existingMesh;

        mesh.matrixAutoUpdate = false;
        const sz = 1;
        mesh.matrix.set(
          mjvGeom.mat[0],
          mjvGeom.mat[1],
          mjvGeom.mat[2] * sz,
          mjvGeom.pos[0],
          mjvGeom.mat[3],
          mjvGeom.mat[4],
          mjvGeom.mat[5] * sz,
          mjvGeom.pos[1],
          mjvGeom.mat[6],
          mjvGeom.mat[7],
          mjvGeom.mat[8] * sz,
          mjvGeom.pos[2],
          0,
          0,
          0,
          1
        );
        mesh.matrixWorldNeedsUpdate = true;

        mjvGeom.delete();
      } else {
        // New mesh - create it with geometry, material, shadows, and textures
        const [, geom] = this.getBufferGeometry(mjvGeom);

        const material = new THREE.MeshPhongMaterial();
        material.color.setRGB(mjvGeom.rgba[0], mjvGeom.rgba[1], mjvGeom.rgba[2]);
        material.opacity = mjvGeom.rgba[3];
        material.transparent = mjvGeom.rgba[3] < 1.0;
        material.userData['originalRgba'] = [
          mjvGeom.rgba[0],
          mjvGeom.rgba[1],
          mjvGeom.rgba[2],
          mjvGeom.rgba[3],
        ];

        mesh = new THREE.Mesh(geom, material);

        // Get geometry name to optimize shadows
        const geomName =
          this.mujoco.mj_id2name(
            this.mjModel,
            this.mujoco.mjtObj.mjOBJ_GEOM.value,
            mjvGeom.objid
          ) || '';

        // Walls and ceilings don't need to cast shadows (performance optimization)
        if (geomName.startsWith('wall_') || geomName.startsWith('ceiling_')) {
          mesh.castShadow = false;
        } else {
          mesh.castShadow = true;
        }
        mesh.receiveShadow = true;

        if (mjvGeom.matid >= 0) {
          if (!this.materialIdToMeshes.has(mjvGeom.matid)) {
            this.materialIdToMeshes.set(mjvGeom.matid, []);
          }
          const materialMeshes = this.materialIdToMeshes.get(mjvGeom.matid);
          materialMeshes?.push(mesh);
        }

        // Try to load texture for new mesh
        if (mjvGeom.matid >= 0) {
          try {
            let matTexid: any = (this.mjModel as any).mat_texid;
            if (typeof matTexid === 'function') {
              matTexid = matTexid();
            }

            const mjNTEXROLE = 3;
            const texId: number = matTexid ? matTexid[mjvGeom.matid * mjNTEXROLE] : -1;

            if (texId >= 0) {
              if (this.textureCache.has(texId)) {
                const texture = this.textureCache.get(texId);
                if (texture) {
                  const meshesWithMaterial = this.materialIdToMeshes.get(mjvGeom.matid);
                  meshesWithMaterial?.forEach((m) => {
                    this.applyTextureToMesh(m, texture, `texture_${texId}`, true);
                  });
                  if (!meshesWithMaterial) {
                    this.applyTextureToMesh(mesh, texture, `texture_${texId}`, true);
                  }
                }
              }
            } else {
              this.tryLoadTextureForMaterial(mjvGeom.matid, material, mesh);
            }
          } catch (e) {
            console.debug(`Could not load texture for material ${mjvGeom.matid}:`, e);
          }
        }

        this.meshes.push(mesh);
        this.scene.add(mesh);

        // Set initial transform for new mesh
        mesh.matrixAutoUpdate = false;
        const sz = 1;
        mesh.matrix.set(
          mjvGeom.mat[0],
          mjvGeom.mat[1],
          mjvGeom.mat[2] * sz,
          mjvGeom.pos[0],
          mjvGeom.mat[3],
          mjvGeom.mat[4],
          mjvGeom.mat[5] * sz,
          mjvGeom.pos[1],
          mjvGeom.mat[6],
          mjvGeom.mat[7],
          mjvGeom.mat[8] * sz,
          mjvGeom.pos[2],
          0,
          0,
          0,
          1
        );
        mesh.matrixWorldNeedsUpdate = true;

        mjvGeom.delete();
      }
    }

    geoms.delete();
    const meshUpdateTime = performance.now() - meshUpdateStart;

    const totalUpdateTime = performance.now() - frameStart;

    // Log performance metrics every 60 frames when playing
    if (this.playing && this.frameCount % 60 === 0 && this.frameCount > 0) {
      const avgPhysicsPerStep = physicsSteps > 0 ? physicsTime / physicsSteps : 0;
      const now = performance.now();
      const timeSinceLastLog = now - this.lastFpsLog;
      const fps = timeSinceLastLog > 0 ? (60 / (timeSinceLastLog / 1000)).toFixed(1) : '0';

      console.log(
        `🚀 [MujocoApp.ts] Frame ${this.frameCount}: FPS=${fps}, total=${totalUpdateTime.toFixed(2)}ms`
      );
      console.log(`   ├─ controls: ${controlsTime.toFixed(2)}ms`);
      console.log(
        `   ├─ physics: ${physicsTime.toFixed(2)}ms (${physicsSteps} steps × ${avgPhysicsPerStep.toFixed(2)}ms/step)`
      );
      console.log(`   ├─ scene update: ${sceneUpdateTime.toFixed(2)}ms`);
      console.log(`   └─ mesh update: ${meshUpdateTime.toFixed(2)}ms`);
      console.log(
        `   mjENBL_SLEEP enabled: ${(this.mjModel.opt.enableflags & this.mujoco.mjtEnableBit.mjENBL_SLEEP.value) !== 0}`
      );

      this.lastFpsLog = now;
    }

    // Center camera on scene after meshes are loaded (do this once, only for top-down mode)
    this.frameCount++;
    if (this.frameCount === 3 && !this.sceneCentered && this.meshes.length > 0) {
      this.sceneCentered = true;
      // Only center if we're in top-down mode (initial load or when switching back)
      if (this.cameraMode === 'top-down') {
        console.log(`📸 Auto-centering camera on scene load (${this.meshes.length} meshes)`);
        this.centerCameraOnScene();
      }
    }

    // Log texture summary periodically
    if (this.frameCount === 60) {
      console.log('\n=== Texture Loading Summary (after 60 frames) ===');
      console.log(`Total meshes: ${this.meshes.length}`);
      console.log(`Cached textures: ${this.textureCacheByFilename.size}`);
      console.log(`Material mappings: ${this.materialToTextureMap.size}`);

      let meshesWithTextures = 0;
      this.meshes.forEach((mesh) => {
        const material = mesh.material;
        if (Array.isArray(material)) {
          if (material.some((mat) => mat instanceof THREE.MeshPhongMaterial && mat.map)) {
            meshesWithTextures++;
          }
        } else if (material instanceof THREE.MeshPhongMaterial && material.map) {
          meshesWithTextures++;
        }
      });
      console.log(`Meshes with textures: ${meshesWithTextures}`);
      console.log('===============================================\n');
    }
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }

  run() {
    const animate = () => {
      try {
        this.update();
        this.render();
      } catch (error) {
        console.error('Simulation error:', error);
      }

      this.frameId = requestAnimationFrame(animate);
    };

    this.frameId = requestAnimationFrame(animate);
  }
}

/**
 * Converts a 3D world position to 2D screen coordinates.
 * Projects a THREE.Vector3 position through the camera to get screen-space coordinates.
 * @param pos - 3D position in world space (THREE.Vector3)
 * @param camera - THREE.js camera used for projection
 * @param width - Width of the viewport in pixels
 * @param height - Height of the viewport in pixels
 * @returns Object with x, y coordinates in screen space (pixels)
 */
function worldPosToScreenPos(
  pos: THREE.Vector3,
  camera: THREE.Camera,
  width: number,
  height: number
): { x: number; y: number } {
  const vector = pos.clone().project(camera);

  // Convert from normalized device coordinates [-1, 1] to screen coordinates
  const x = ((vector.x + 1) / 2) * width;
  const y = (-(vector.y - 1) / 2) * height;

  return { x, y };
}
